// src/components/SEO.jsx - React 19 compatible SEO component with canonical links
import { useSEO } from '../hooks/useSEO';

function SEO(props) {
  useSEO(props);
  return null; // This component doesn't render anything
}


export const eventsPageSEOConfigs = {
  // General events page
  events: {
    title: "Exclusive Community Events - Swedish Fika, Monthly Fika, Signature Weekly Fika, Curated Fika, Speed Fika, and Builder Coffee Match | Connect & Co",
    description: "Connect & Co is a community-building hub that helps individuals forge authentic friendships through curated Swedish Fika experiences in Tokyo, Osaka, and beyond.",
    keywords: "exclusive community events, speed fika, Swedish fika, diverse experiences, happy hour networking, themed discussions, priority access, intimate gatherings, international community",
    canonical: "https://connectco.app/events"
  },

  // City-specific pages
  cityEvents: (cityName, citySlug) => ({
    title: `${cityName} Community Events - Swedish Fika,  Monthly Fika, Signature Weekly Fika, Curated Fika, Speed Fika, and Builder Coffee Match | Connect & Co`,
    description: `Join exclusive community events in ${cityName}. Build authentic friendships through Swedish Fika experiences , forest and river walks, and intimate authentic gatherings.`,
    keywords: `${cityName} events, ${cityName} networking, ${cityName} fika, ${cityName} community events, international community ${cityName}`,
    canonical: `https://connectco.app/events/city/${citySlug}`
  }),

  // Event type specific pages
  eventTypePages: {
    'fika': {
      title: "Swedish Fika Events - Authentic Coffee Culture & Connection | Connect & Co",
      description: "Experience authentic Swedish Fika culture. Join intimate coffee gatherings focused on meaningful conversation, community building, and genuine connection.",
      keywords: "Swedish fika, coffee culture, authentic connections, Swedish traditions, meaningful conversations, community building"
    },
    'forest-walks': {
      title: "Forest Walks & Nature Networking - Mindful Outdoor Connection | Connect & Co", 
      description: "Connect with nature and like-minded people. Join guided forest and rivers walks combining mindfulness, wellness, and authentic networking.",
      keywords: "forest walks, nature networking, mindful walking, outdoor events, wellness networking"
    },
    'happy-hour': {
      title: "Intimate Happy Hour Networking - Quality Over Quantity | Connect & Co",
      description: "Skip crowded networking events. Join intimate happy hours designed for meaningful professional connections, community building, authentic friendships, authentic conversations.",
      keywords: "intimate networking, community building, happy hour events, professional networking, quality connections"
    },
    'growth-circle': {
      title: "Growth Circle Discussions - Personal Development & Community | Connect & Co",
      description: "Join thought-provoking discussions on personal growth, professional development, and building meaningful community connections and community building.",
      keywords: "growth circle, personal development, professional growth, community discussions, community building"
    },
    'signature-fika': {
    title: "Signature Fika - Weekly Swedish Coffee Community | Connect & Co",
    description: "Your weekly social community gatherings. Join 10-20 exceptional people every Wednesday for authentic Swedish Fika tradition at premium venues. Members-only community.",
    keywords: "signature fika, weekly community, social infrastructure, Swedish tradition, premium venues, members-only, weekly networking, community building, predictable community"
    },
    'curated-fika': {
      title: "Curated Fika - AI-Assisted Intimate Coffee Connections | Connect & Co", 
      description: "Ultra-premium intimate Swedish Fika experience. AI-assisted curation matches 3-4 compatible individuals for meaningful 90-minute conversations at premium venues.",
      keywords: "community building, curated fika, AI-assisted matching, intimate connections, premium coffee experiences, Swedish tradition, quality networking, small group gatherings"
    },
    'speed-fika': {
      title: "Speed Fika Connection - Structured Deep Conversation Events | Connect & Co",
      description: "Revolutionary Speed Fika Connection using 12 psychology-based questions across 4 rotating rounds. Build authentic relationships through structured meaningful dialogue.",
      keywords: "speed fika, structured conversations, psychology-based questions, deep connection, authentic relationships, rotating discussions, community building, meaningful networking"
    }
  }
};




// Coffee Guide SEO Configurations
export const coffeeGuideSEOConfigs = {
  // Main coffee guide page
  main: {
    title: "Specialty Coffee Guide | Tokyo, Osaka, Lisbon, Porto | Connect & Co",
    description: "Discover 150+ exceptional specialty coffee shops across Tokyo, Osaka, Lisbon, and Porto curated by the Connect & Co community. Your guide to third wave coffee culture worldwide.",
    keywords: "specialty coffee guide, third wave coffee, best cafes worldwide, coffee culture, tokyo coffee, osaka coffee, lisbon coffee, porto coffee, coffee roasters, artisan coffee, curated coffee shops, global coffee guide, community recommended cafes",
    url: "https://connectco.app/coffee-guide",
    canonical: "https://connectco.app/coffee-guide",
    robots: "index, follow",
    openGraph: {
      title: "Specialty Coffee Guide - Tokyo, Osaka, Lisbon, Porto",
      description: "Explore 150+ exceptional specialty coffee shops curated by our global community across four cities.",
      url: "https://connectco.app/coffee-guide",
      image: "https://connectco.app/coffee-guide-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: "Specialty Coffee Guide | Connect & Co",
      description: "Curated coffee experiences in Tokyo, Osaka, Lisbon & Porto",
      image: "https://connectco.app/coffee-guide-og.jpg"
    },
    additionalMeta: [
      { name: "author", content: "Connect & Co Community" },
      { name: "publisher", content: "Connect & Co" },
      { name: "application-name", content: "Connect & Co Coffee Guide" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "target-audience", content: "Coffee enthusiasts, travelers, digital nomads, specialty coffee lovers" }
    ]
  },

  // Tokyo
  tokyo: {
    title: "Tokyo's Best Third Wave Coffee Shops | 150+ Specialty Coffee Guide | Connect & Co",
    description: "Discover 150+ exceptional specialty coffee shops in Tokyo curated by the Connect & Co community. Find third wave cafes in Shibuya, Harajuku, Omotesando, Nakameguro and beyond with ratings, reviews, and locations.",
    keywords: "tokyo coffee, third wave coffee tokyo, specialty coffee tokyo, best cafes tokyo, tokyo coffee shops, coffee roasters tokyo, shibuya coffee, harajuku coffee, omotesando coffee, nakameguro coffee, tokyo fika, japanese coffee culture, artisan coffee tokyo, coffee guide tokyo",
    url: "https://connectco.app/coffee-guide/tokyo",
    canonical: "https://connectco.app/coffee-guide/tokyo",
    robots: "index, follow",
    openGraph: {
      title: "Tokyo's Best Third Wave Coffee Shops - 150+ Curated Cafes",
      description: "Discover Tokyo's exceptional specialty coffee scene curated by the Connect & Co community. Explore cafes by neighborhood with ratings and reviews.",
      url: "https://connectco.app/coffee-guide/tokyo",
      image: "https://connectco.app/tokyo-coffee-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: "Tokyo's Best Third Wave Coffee Shops | 150+ Cafes",
      description: "Community-curated specialty coffee shops across Tokyo neighborhoods",
      image: "https://connectco.app/tokyo-coffee-og.jpg"
    },
    additionalMeta: [
      { name: "geo.region", content: "JP-13" },
      { name: "geo.placename", content: "Tokyo" },
      { name: "geo.position", content: "35.6762;139.6503" },
      { name: "ICBM", content: "35.6762, 139.6503" }
    ]
  },

  // Osaka
  osaka: {
    title: "Osaka's Best Specialty Coffee Shops | Artisan Coffee Guide | Connect & Co",
    description: "Discover exceptional specialty coffee shops in Osaka curated by the Connect & Co community. Find artisan cafes in Namba, Kitahama, Umeda and throughout the city with reviews and locations.",
    keywords: "osaka coffee, specialty coffee osaka, best cafes osaka, osaka coffee shops, coffee roasters osaka, namba coffee, kitahama coffee, umeda coffee, osaka fika, japanese coffee culture, artisan coffee osaka, coffee guide osaka",
    url: "https://connectco.app/coffee-guide/osaka",
    canonical: "https://connectco.app/coffee-guide/osaka",
    robots: "index, follow",
    openGraph: {
      title: "Osaka's Best Specialty Coffee Shops - Curated Guide",
      description: "Discover Osaka's vibrant specialty coffee culture curated by the Connect & Co community.",
      url: "https://connectco.app/coffee-guide/osaka",
      image: "https://connectco.app/osaka-coffee-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: "Osaka's Best Specialty Coffee Shops",
      description: "Curated artisan coffee experiences in Osaka",
      image: "https://connectco.app/osaka-coffee-og.jpg"
    },
    additionalMeta: [
      { name: "geo.region", content: "JP-27" },
      { name: "geo.placename", content: "Osaka" },
      { name: "geo.position", content: "34.6937;135.5023" },
      { name: "ICBM", content: "34.6937, 135.5023" }
    ]
  },

  // Lisbon
  lisbon: {
    title: "Lisbon's Best Specialty Coffee Shops | Coffee Culture Guide | Connect & Co",
    description: "Discover exceptional specialty coffee shops in Lisbon curated by the Connect & Co community. Find charming cafes in Baixa, Chiado, Santos, Principe Real and beyond with reviews and recommendations.",
    keywords: "lisbon coffee, specialty coffee lisbon, best cafes lisbon, lisbon coffee shops, coffee roasters lisbon, baixa coffee, chiado coffee, santos coffee, lisbon fika, portuguese coffee culture, artisan coffee lisbon, coffee guide lisbon",
    url: "https://connectco.app/coffee-guide/lisbon",
    canonical: "https://connectco.app/coffee-guide/lisbon",
    robots: "index, follow",
    openGraph: {
      title: "Lisbon's Best Specialty Coffee Shops - Curated Guide",
      description: "Discover Lisbon's charming specialty coffee scene curated by the Connect & Co community.",
      url: "https://connectco.app/coffee-guide/lisbon",
      image: "https://connectco.app/lisbon-coffee-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: "Lisbon's Best Specialty Coffee Shops",
      description: "Curated coffee experiences in Lisbon's most charming neighborhoods",
      image: "https://connectco.app/lisbon-coffee-og.jpg"
    },
    additionalMeta: [
      { name: "geo.region", content: "PT-11" },
      { name: "geo.placename", content: "Lisbon" },
      { name: "geo.position", content: "38.7223;-9.1393" },
      { name: "ICBM", content: "38.7223, -9.1393" }
    ]
  },

  // Porto
  porto: {
    title: "Porto's Best Specialty Coffee Shops | Coffee Culture Guide | Connect & Co",
    description: "Discover exceptional specialty coffee shops in Porto curated by the Connect & Co community. Find charming cafes in Ribeira, Cedofeita, Foz and beyond with reviews and local recommendations.",
    keywords: "porto coffee, specialty coffee porto, best cafes porto, porto coffee shops, coffee roasters porto, ribeira coffee, cedofeita coffee, foz coffee, porto fika, portuguese coffee culture, artisan coffee porto, coffee guide porto",
    url: "https://connectco.app/coffee-guide/porto",
    canonical: "https://connectco.app/coffee-guide/porto",
    robots: "index, follow",
    openGraph: {
      title: "Porto's Best Specialty Coffee Shops - Curated Guide",
      description: "Discover Porto's charming specialty coffee scene curated by the Connect & Co community.",
      url: "https://connectco.app/coffee-guide/porto",
      image: "https://connectco.app/porto-coffee-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: "Porto's Best Specialty Coffee Shops",
      description: "Curated coffee experiences in Porto's most authentic neighborhoods",
      image: "https://connectco.app/porto-coffee-og.jpg"
    },
    additionalMeta: [
      { name: "geo.region", content: "PT-13" },
      { name: "geo.placename", content: "Porto" },
      { name: "geo.position", content: "41.1579;-8.6291" },
      { name: "ICBM", content: "41.1579, -8.6291" }
    ]
  }
};



// Helper function to get coffee guide SEO config by city
export const getCoffeeGuideSEO = (city) => {
  if (!city) return coffeeGuideSEOConfigs.main;
  return coffeeGuideSEOConfigs[city.toLowerCase()] || coffeeGuideSEOConfigs.main;
};

export const createCoffeeGuideBreadcrumb = (city) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://connectco.app"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Coffee Guide",
      "item": "https://connectco.app/coffee-guide"
    },
    ...(city ? [{
      "@type": "ListItem",
      "position": 3,
      "name": `${city.charAt(0).toUpperCase() + city.slice(1)} Coffee`,
      "item": `https://connectco.app/coffee-guide/${city}`
    }] : [])
  ]
});

// FAQ Schema for Coffee Guides
export const coffeeGuideFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What makes a coffee shop 'third wave'?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Third wave coffee shops treat coffee as an artisanal product rather than a commodity. They focus on single-origin beans, precise brewing methods like pour-over and siphon, direct trade relationships with farmers, lighter roasts that highlight the unique flavor profiles of each coffee, and skilled baristas who understand coffee as a craft. These shops emphasize transparency, sustainability, and the entire coffee journey from farm to cup."
      }
    },
    {
      "@type": "Question",
      "name": "How are these coffee shops curated?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "All coffee shops in our guide are recommended by the Connect & Co community - real coffee enthusiasts, locals, travelers, and digital nomads who visit these cafes regularly. Each recommendation includes ratings for coffee quality, atmosphere, service, and value. We prioritize specialty coffee roasters, third wave cafes, and places where the community genuinely gathers for authentic fika experiences."
      }
    },
    {
      "@type": "Question",
      "name": "What is the best neighborhood for specialty coffee in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Tokyo has exceptional specialty coffee across many neighborhoods. Shibuya and Harajuku offer dense concentrations of innovative cafes like Streamer Coffee Company and Koffee Mameya. Nakameguro along the canal features local favorites like Onibus Coffee. Omotesando has premium experiences, while areas like Kiyosumi-Shirakawa have become coffee destinations with roasteries and third wave pioneers."
      }
    },
    {
      "@type": "Question",
      "name": "Are these coffee guides free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! All our coffee guides are completely free and community-curated. We believe great coffee should be accessible to everyone. Our guides include detailed information, ratings, locations, and community reviews to help you discover exceptional specialty coffee in each city."
      }
    },
    {
      "@type": "Question",
      "name": "Can I submit my own coffee shop recommendations?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely! We encourage community contributions. If you've discovered an exceptional specialty coffee shop in Tokyo, Osaka, Lisbon, Porto, or any city where you've experienced great fika, you can submit your discovery through our submission form. Share details about the coffee quality, atmosphere, and what makes it special."
      }
    },
    {
      "@type": "Question",
      "name": "What's the difference between a coffee shop and a third wave cafe?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Traditional coffee shops primarily serve coffee as a beverage alongside other menu items. Third wave cafes elevate coffee to an artisanal experience - they source high-quality single-origin beans, employ precise brewing techniques, train baristas extensively, and focus on highlighting the unique characteristics of each coffee. It's the difference between coffee as fuel versus coffee as a culinary experience worthy of appreciation."
      }
    },
    {
      "@type": "Question",
      "name": "How often are these coffee guides updated?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our coffee guides are continuously updated as community members discover new cafes and provide feedback. We review submissions regularly and add new exceptional coffee shops as they're discovered. The community also updates ratings and reviews based on their recent experiences, ensuring the guides remain current and accurate."
      }
    },
    {
      "@type": "Question",
      "name": "Do you include coffee shop opening hours and contact information?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes! Each coffee shop listing includes practical information like opening hours, contact details, location maps, price range, and special features. We also note if reservations are required (like at Koffee Mameya in Tokyo) or if there are unique experiences like coffee tastings or roastery tours available."
      }
    }
  ]
};


// WebSite Schema for Coffee Guide Section
export const coffeeGuideWebsiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Connect & Co Coffee Guide",
  "alternateName": "Specialty Coffee Guide",
  "url": "https://connectco.app/coffee-guide",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://connectco.app/coffee-guide?search={search_term_string}"
    },
    "query-input": "required name=search_term_string"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png"
    }
  }
};

export const coffeeGuideStructuredData = {
  // Tokyo Coffee Guide Structured Data
  tokyo: {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Tokyo's Best Third Wave Specialty Coffee Shops",
    "description": "Community-curated list of 150+ exceptional specialty coffee shops across Tokyo neighborhoods including Shibuya, Harajuku, Omotesando, Nakameguro, and more",
    "url": "https://connectco.app/coffee-guide/tokyo",
    "numberOfItems": 150,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "itemListElement": [
      // Top featured coffee shops - you can populate more from your database
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Koffee Mameya",
          "description": "Tokyo's most exclusive coffee tasting experience with reservation-only service",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "4-15-3 Jingumae",
            "addressLocality": "Shibuya",
            "addressRegion": "Tokyo",
            "addressCountry": "JP"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "35.6681",
            "longitude": "139.7098"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "¥¥¥",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.9",
            "bestRating": "5",
            "reviewCount": "247"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Streamer Coffee Company",
          "description": "Award-winning latte art and exceptional single-origin coffee in Shibuya",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Shibuya",
            "addressRegion": "Tokyo",
            "addressCountry": "JP"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "¥¥"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Onibus Coffee",
          "description": "Nakameguro's beloved local roaster with multiple locations across Tokyo",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Nakameguro",
            "addressRegion": "Tokyo",
            "addressCountry": "JP"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "¥¥"
        }
      }
      // Add more coffee shops as needed from your database
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": "Coffee enthusiasts, tourists, digital nomads, specialty coffee lovers"
    },
    "about": [
      {
        "@type": "Thing",
        "name": "Third Wave Coffee"
      },
      {
        "@type": "Thing",
        "name": "Specialty Coffee Culture"
      },
      {
        "@type": "Thing",
        "name": "Tokyo Coffee Scene"
      }
    ]
  },

  // Osaka Coffee Guide Structured Data
  osaka: {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Osaka's Best Specialty Coffee Shops",
    "description": "Community-curated list of exceptional specialty coffee shops across Osaka neighborhoods including Namba, Kitahama, Umeda, and beyond",
    "url": "https://connectco.app/coffee-guide/osaka",
    "numberOfItems": 45,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Mel Coffee Roasters",
          "description": "Osaka's premier specialty coffee roaster with exceptional single-origin selection",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Kitahama",
            "addressRegion": "Osaka",
            "addressCountry": "JP"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "34.6851",
            "longitude": "135.5089"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "¥¥"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Brooklyn Roasting Company Kitahama",
          "description": "New York-style coffee roastery in the heart of Osaka's business district",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Kitahama",
            "addressRegion": "Osaka",
            "addressCountry": "JP"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "¥¥"
        }
      }
      // Add more Osaka coffee shops
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": "Coffee enthusiasts, travelers, business professionals, specialty coffee lovers"
    }
  },

  // Lisbon Coffee Guide Structured Data
  lisbon: {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Lisbon's Best Specialty Coffee Shops",
    "description": "Community-curated list of exceptional specialty coffee shops across Lisbon neighborhoods including Baixa, Chiado, Santos, Principe Real, and beyond",
    "url": "https://connectco.app/coffee-guide/lisbon",
    "numberOfItems": 38,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Fábrica Coffee Roasters",
          "description": "Lisbon's pioneering specialty coffee roastery in the heart of Santos",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Rua das Flores 63",
            "addressLocality": "Santos",
            "addressRegion": "Lisbon",
            "postalCode": "1200-195",
            "addressCountry": "PT"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "38.7069",
            "longitude": "-9.1474"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "€€",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.7",
            "bestRating": "5",
            "reviewCount": "189"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Copenhagen Coffee Lab",
          "description": "Nordic-inspired specialty coffee in Lisbon's trendy Principe Real neighborhood",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Principe Real",
            "addressRegion": "Lisbon",
            "addressCountry": "PT"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "€€"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Hello, Kristof",
          "description": "Charming specialty coffee shop in Chiado with exceptional pastries",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Chiado",
            "addressRegion": "Lisbon",
            "addressCountry": "PT"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "€€"
        }
      }
      // Add more Lisbon coffee shops
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": "Coffee enthusiasts, digital nomads, travelers, expats, specialty coffee lovers"
    }
  },

  // Porto Coffee Guide Structured Data
  porto: {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Porto's Best Specialty Coffee Shops",
    "description": "Community-curated list of exceptional specialty coffee shops across Porto neighborhoods including Ribeira, Cedofeita, Foz, and beyond",
    "url": "https://connectco.app/coffee-guide/porto",
    "numberOfItems": 28,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Combi Coffee Roasters",
          "description": "Porto's beloved specialty coffee roastery with exceptional single-origin selection",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Cedofeita",
            "addressRegion": "Porto",
            "addressCountry": "PT"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "41.1559",
            "longitude": "-8.6283"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "€€",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "bestRating": "5",
            "reviewCount": "156"
          }
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "CafeOrCoffeeShop",
          "name": "Embaixada",
          "description": "Stunning specialty coffee shop in a historic palace with beautiful courtyard",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Centro",
            "addressRegion": "Porto",
            "addressCountry": "PT"
          },
          "servesCuisine": "Specialty Coffee",
          "priceRange": "€€"
        }
      }
      // Add more Porto coffee shops
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": "Coffee enthusiasts, travelers, digital nomads, specialty coffee lovers"
    }
  },

  // Main Coffee Guide (all cities) Structured Data
  main: {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Global Specialty Coffee Guide",
    "description": "Community-curated specialty coffee guides for Tokyo, Osaka, Lisbon, and Porto featuring 150+ exceptional third wave coffee shops",
    "url": "https://connectco.app/coffee-guide",
    "mainEntity": {
      "@type": "ItemList",
      "name": "Featured Cities Coffee Guides",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@type": "WebPage",
            "name": "Tokyo Coffee Guide",
            "description": "150+ specialty coffee shops in Tokyo",
            "url": "https://connectco.app/coffee-guide/tokyo"
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@type": "WebPage",
            "name": "Osaka Coffee Guide",
            "description": "45+ specialty coffee shops in Osaka",
            "url": "https://connectco.app/coffee-guide/osaka"
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@type": "WebPage",
            "name": "Lisbon Coffee Guide",
            "description": "38+ specialty coffee shops in Lisbon",
            "url": "https://connectco.app/coffee-guide/lisbon"
          }
        },
        {
          "@type": "ListItem",
          "position": 4,
          "item": {
            "@type": "WebPage",
            "name": "Porto Coffee Guide",
            "description": "28+ specialty coffee shops in Porto",
            "url": "https://connectco.app/coffee-guide/porto"
          }
        }
      ]
    },
    "author": {
      "@type": "Organization",
      "name": "Connect & Co",
      "url": "https://connectco.app"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Connect & Co",
      "logo": {
        "@type": "ImageObject",
        "url": "https://connectco.app/logo.png"
      }
    }
  }
};

// Helper function to get structured data for a specific city
export const getCoffeeGuideStructuredData = (city = null) => {
  if (!city) return coffeeGuideStructuredData.main;
  const cityKey = city.toLowerCase();
  return coffeeGuideStructuredData[cityKey] || coffeeGuideStructuredData.main;
};

// EXISTING FUNCTION - REPLACE with enhanced version in SEO.jsx
export const createEventStructuredData = (event) => {
  if (!event) return null;

  // GCS base URL — mirrors IMAGE_CONFIG.GCS_BASE_URL in utils/images.js
  const GCS_BASE = 'https://storage.googleapis.com/connectco-storage';

  // Parse end datetime from time range string e.g. "10:00 AM - 12:30 PM" + fullDate "2026-06-14"
  // Schema.org requires ISO 8601 — event.next is human-readable and cannot be used
  const parseEndDateTime = (fullDate, timeString) => {
    if (!fullDate || !timeString) return fullDate || null;
    try {
      const parts = timeString.split(' - ');
      if (parts.length < 2) return fullDate;
      const endTimeStr = parts[1].trim();
      const [time, period] = endTimeStr.split(' ');
      const [hoursStr, minutesStr] = time.split(':');
      let hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr || '0', 10);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      return `${fullDate}T${hh}:${mm}:00+09:00`;
    } catch (e) {
      return fullDate;
    }
  };

  // Safe price extraction — "0" for free/missing, numeric string for paid events
  const getOfferPrice = (cost) => {
    if (!cost || cost.toLowerCase().includes('free') || cost.toLowerCase().includes('included')) return '0';
    const numeric = cost.replace(/[^0-9]/g, '');
    return numeric || '0';
  };

  // Use ISO dates — eventDateTime is a full ISO string, fullDate is YYYY-MM-DD
  const startDate = event.eventDateTime || event.fullDate || event.next;
  const endDate = parseEndDateTime(event.fullDate, event.time) || startDate;
  const eventUrl = 'https://connectco.app/events/' + event.slug;

  // Image from GCS (real CDN), not connectco.app which doesn't serve these files
  const imageUrl = event.iconImage
    ? GCS_BASE + event.iconImage
    : GCS_BASE + '/images/events/ConnectCoMonthlyFika.jpg';

  const baseStructuredData = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": event.title,
    "description": event.description,
    "url": eventUrl,
    "startDate": startDate,
    "endDate": endDate,
    "eventStatus": event.status === 'open'
      ? "https://schema.org/EventScheduled"
      : "https://schema.org/EventPostponed",
    "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
    "image": imageUrl,
    "performer": {
      "@type": "Organization",
      "name": "Connect & Co",
      "url": "https://connectco.app"
    },
    "location": {
      "@type": "Place",
      "name": event.venue?.name || event.location,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": event.venue?.fullAddress || event.location,
        "addressLocality": event.cityDisplay || "Tokyo",
        "addressRegion": event.cityDisplay || "Tokyo",
        "addressCountry": "Japan"
      }
    },
    "organizer": {
      "@type": "Organization",
      "name": "Connect & Co",
      "url": "https://connectco.app",
      "legalName": "Pukka Holdings LLC"
    },
    "offers": {
      "@type": "Offer",
      "name": "Event Registration",
      "price": getOfferPrice(event.cost),
      "priceCurrency": event.currency || "JPY",
      "availability": event.status === 'open'
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      "url": eventUrl,
      "validFrom": new Date().toISOString()
    },
    "maximumAttendeeCapacity": event.capacity,
    "audience": {
      "@type": "Audience",
      "audienceType": "Expats, digital nomads, and bilingual locals in Tokyo"
    },
    "keywords": event.tags?.join(", ") || "",
    "isAccessibleForFree": !event.cost || event.cost.toLowerCase().includes('free'),
    "inLanguage": "en-US"
  };

  // Per-event-type FAQ schemas for AI answer engine extraction
  // Each block adds a mainEntity FAQPage so AI engines can surface direct answers

  if (event.eventType === 'monthly-fika' || event.slug.includes('monthly-fika')) {
    baseStructuredData.mainEntity = {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is ConnectCo's Monthly Fika?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": event.title + " is " + event.description + " It takes place on " + event.next + " at " + event.time + ". This is the easiest way to meet your first friends in Tokyo — no agenda, no networking, just good conversation over coffee."
          }
        },
        {
          "@type": "Question",
          "name": "Who attends Monthly Fika in Tokyo?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Monthly Fika welcomes expats, digital nomads, and bilingual locals. The crowd is internationally-minded — mostly English-speaking professionals aged 25–45 who value real friendship over transactional networking."
          }
        },
        {
          "@type": "Question",
          "name": "How much does it cost to attend a fika event in Tokyo?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": (event.cost ? "This event costs " + event.cost + "." : "This event is free to attend.") + " You pay for your own coffee and any food at the venue. No hidden fees."
          }
        }
      ]
    };
  }

  if (event.eventType === 'signature-fika' || event.slug.includes('weekly-fika') || event.slug.includes('signature-fika')) {
    baseStructuredData.mainEntity = {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the Weekly Signature Fika?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": event.title + " is " + event.description + " Taking place every Wednesday, limited to " + event.capacity + " people. Designed to build a consistent social rhythm where familiar faces become real friends."
          }
        },
        {
          "@type": "Question",
          "name": "Is the Weekly Signature Fika members-only?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Weekly Signature Fika is available to ConnectCo paid subscribers. A monthly subscription gives you access to all weekly events plus the full suite of ConnectCo gatherings."
          }
        },
        {
          "@type": "Question",
          "name": "How do I join ConnectCo's weekly fika in Tokyo?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Sign up for a ConnectCo membership at connectco.app/subscription. Monthly, 6-month, and yearly plans are available. Once subscribed, all weekly events are included."
          }
        }
      ]
    };
  }

  if (event.eventType === 'curated-fika' || event.slug.includes('curated-fika')) {
    baseStructuredData.mainEntity = {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Curated Fika?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Curated Fika is " + event.description + " Groups of 3–4 people are matched by interest and personality for 90 minutes of deep, unhurried conversation at premium Tokyo venues. Application required."
          }
        },
        {
          "@type": "Question",
          "name": "How are people matched for Curated Fika?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ConnectCo uses AI-assisted human curation to match participants by shared interests, conversation style, values, and connection goals. The aim is optimal group chemistry — people who will genuinely enjoy each other's company."
          }
        },
        {
          "@type": "Question",
          "name": "How do I apply for Curated Fika?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Apply at connectco.app/curated-fika-signup. The application asks about your interests, conversation preferences, and what you are looking for. Accepted applicants are notified when a matching group is formed."
          }
        }
      ]
    };
  }

  if (event.eventType === 'happy-hour' || event.slug.includes('happy-hour')) {
    baseStructuredData.mainEntity = {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is ConnectCo's Happy Hour in Tokyo?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": event.title + " is " + event.description + " Taking place on " + event.next + " at " + event.time + " in " + (event.cityDisplay || 'Tokyo') + ". Low-key, no agenda, good crowd — after-work drinks where you actually want to stay."
          }
        },
        {
          "@type": "Question",
          "name": "Who goes to ConnectCo Happy Hours?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "A mix of expats, digital nomads, and Tokyo locals who are internationally-minded. No cliques, no business cards — just people who enjoy good conversation and a relaxed evening out."
          }
        },
        {
          "@type": "Question",
          "name": "Do I need a membership to attend Happy Hour?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": event.requiresSubscription
              ? "A ConnectCo subscription is required. Members get 24-hour early access to all events."
              : "This event is open to everyone. No membership required — just show up."
          }
        }
      ]
    };
  }

  if (event.eventType === 'river-walks' || event.slug.includes('walk') || event.slug.includes('meguro')) {
    baseStructuredData.mainEntity = {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is the " + event.title + "?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": event.title + " is " + event.description + " Taking place on " + event.next + " at " + event.time + ". A scenic walk through Tokyo that ends with optional coffee."
          }
        },
        {
          "@type": "Question",
          "name": "When and where does this Tokyo walk start?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "The walk begins on " + event.next + " at " + event.time + ". Meeting point: " + event.location + "."
          }
        },
        {
          "@type": "Question",
          "name": "How much does this Tokyo community walk cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": (event.cost ? "This walk costs " + event.cost + "." : "This community walk is free to attend.") + " Optional coffee is at your own expense (typically ¥400–800)."
          }
        }
      ]
    };
  }

  if (event.eventType === 'speed-fika-connection' || event.slug.includes('speed-fika')) {
    baseStructuredData.mainEntity = {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Speed Fika Connection?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": event.title + " is " + event.description + " Using 12 psychology-based questions across 4 rotating rounds to build authentic relationships in one evening."
          }
        },
        {
          "@type": "Question",
          "name": "How much does Speed Fika cost?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": event.cost ? "This event costs " + event.cost + "." : "This event is free to attend."
          }
        },
        {
          "@type": "Question",
          "name": "What makes Speed Fika different from regular networking?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Speed Fika uses structured psychology-based questions to move from surface-level chat to meaningful connection. Four 20-minute rounds with rotating groups of 3–4 people create authentic relationships in a single evening."
          }
        }
      ]
    };
  }

  return baseStructuredData;
};



// Blog Post Structured Data — centralised function used by all article components
// Fixes: @type Article→BlogPosting, GCS image URLs, canonical prop, adds optional FAQ schema
export const createBlogPostStructuredData = (config) => {
  if (!config) return null;

  // GCS base URL — mirrors IMAGE_CONFIG.GCS_BASE_URL in utils/images.js
  const GCS_BASE = 'https://storage.googleapis.com/connectco-storage';

  // Build image URL: full https:// URLs used as-is; filenames get GCS blog path prepended
  const imageUrl = config.imageFilename && config.imageFilename.startsWith('https://')
    ? config.imageFilename
    : GCS_BASE + '/images/blog/' + (config.imageFilename || 'fika-coffee-meeting-story.jpg');

  const canonicalUrl = 'https://connectco.app/blog/' + config.slug;

  // Parse readTimeMinutes from either a number or an existing "PT4M" string
  const timeRequired = config.readTimeMinutes
    ? 'PT' + config.readTimeMinutes + 'M'
    : (config.timeRequired || 'PT5M');

  const blogPosting = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": config.title,
    "description": config.description,
    "url": canonicalUrl,
    "image": imageUrl,
    "author": {
      "@type": "Person",
      "name": config.author || "Mark Jerrick",
      "url": "https://connectco.app/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Connect & Co",
      "legalName": "Pukka Holdings LLC",
      "logo": {
        "@type": "ImageObject",
        "url": GCS_BASE + "/images/branding/ampersand_logo.png"
      }
    },
    "datePublished": config.datePublished,
    "dateModified": config.dateModified || config.datePublished,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "articleSection": config.articleSection || "Community",
    "keywords": Array.isArray(config.keywords)
      ? config.keywords.join(", ")
      : (config.keywords || ""),
    "wordCount": config.wordCount,
    "timeRequired": timeRequired,
    "inLanguage": "en-US"
  };

  // If FAQs supplied, return array with separate FAQPage schema alongside the BlogPosting
  if (config.faqs && config.faqs.length > 0) {
    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": config.faqs.map(function(faq) {
        return {
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        };
      })
    };
    return [blogPosting, faqPage];
  }

  return [blogPosting];
};


// 3. Events List Page Structured Data
export const createEventsListingStructuredData = (events, city = null, filter = null) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": city ? `${city} Community Events` : "Connect & Co Community Events",
  "description": city 
    ? `Upcoming community events in ${city} with priority access for members`
    : "Upcoming community events with priority access for members",
  "numberOfItems": events.length,
  "itemListElement": events.slice(0, 10).map((event, index) => ({ // Limit to first 10 for performance
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Event",
      "name": event.title,
      "url": `https://connectco.app/events/${event.slug}`,
      "startDate": event.fullDate || event.next,
      "location": {
        "@type": "Place",
        "name": event.location,
        "address": event.cityDisplay || event.city
      }
    }
  }))
});



// SEO configurations for different pages with canonical links
export const seoConfigs = {
  
 home: {
  title: "Build Authentic Friendships Through Swedish Fika | Happiness Begins With Connection | Connect & Co",
  description: "Where strangers become friends in Tokyo, Osaka, Lisbon, Porto, and expanding worldwide. Happiness begins with authentic connection through Monthly Fika, Weekly Signature Fika, intimate Curated Fika, and genuine community experiences. Join locals, international professionals, digital nomads, and culture enthusiasts finding real friendship through Swedish Fika tradition.",
  keywords: "friendship-based community, authentic friendships, meet new friends, building friendships together, genuine friendship, happiness through connection, authentic connection, real friendships tokyo, meaningful friendships, find your people, find your tribe, swedish fika tokyo, monthly fika, weekly signature fika, curated fika, friendship building, adult friendship groups, making real friends after 30, international friendships tokyo, expat friendships, digital nomad community, culture enthusiasts, locals welcome, friendship community building, happiness community, friendship alternatives to networking, authentic friendship experiences, social infrastructure tokyo, community touchpoints, global friendship community, friendship events tokyo",
  url: "https://connectco.app",
  canonical: "https://connectco.app",
  openGraph: {
    title: "Build Authentic Friendships Through Swedish Fika | Happiness Begins With Connection",
    description: "Building authentic friendships together through Swedish Fika tradition. Join locals, international professionals, digital nomads, and culture enthusiasts finding genuine friendship and happiness worldwide.",
    url: "https://connectco.app",
    image: "https://connectco.app/og-image.jpg",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    title: "Build Authentic Friendships Through Swedish Fika | Connect & Co",
    description: "Where strangers become friends . Happiness begins with authentic connection through Swedish Fika tradition. Meet new friends.",
    image: "https://connectco.app/twitter-image.jpg"
  }
},

monthlyFika: {
  title: "Monthly Fika - Build Authentic Friendships Through Free Swedish Coffee Tradition | 40+ People, Twice Monthly | Connect & Co",
  description: "Where strangers become friends  together through Monthly Fika tradition. Join 40+ locals, international professionals, digital nomads, and culture enthusiasts twice monthly for genuine friendship and happiness. Meet new friends, open to all, authentic Swedish coffee culture in premium Tokyo cafés. Real friendships start here.",
  keywords: "monthly fika tokyo, authentic friendships, building friendships, free fika events, swedish fika tradition, genuine friendship tokyo, real friendships, meaningful friendships, twice monthly fika, authentic coffee culture, international community tokyo, friendship building tokyo, making friends tokyo, adult friendship groups, friendship events tokyo, locals welcome fika, digital nomads tokyo, culture enthusiasts community, free community events, authentic connection tokyo, happiness through friendship, friendship alternatives networking, 40 person gatherings, large group friendship, community building tokyo, free swedish fika, authentic networking tokyo",
  url: "https://connectco.app/monthly-fika",
  canonical: "https://connectco.app/monthly-fika",
  openGraph: {
    title: "Monthly Fika - Build Authentic Friendships Through Free Swedish Coffee Tradition",
    description: "Where strangers become friends together through Monthly Fika. Join 40+ people twice monthly for genuine friendship and happiness through authentic Swedish coffee culture.",
    url: "https://connectco.app/monthly-fika",
    image: "https://connectco.app/monthly-fika-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "Monthly Fika - Build Authentic Friendships | Free Swedish Coffee Tradition",
    description: "Join 40+ people twice monthly for genuine friendship and happiness through authentic Swedish Fika tradition in Tokyo.",
    image: "https://connectco.app/monthly-fika-og.jpg"
  }
},

kindMoments: {
  title: "Kind Moments — Stories of Human Kindness from Around the World | Connect & Co",
  description:
    "A global archive of small acts that changed someone's day — or life. Share your story of kindness: something you gave, received, or witnessed. Every country welcome.",
  keywords:
    "kindness stories, random acts of kindness, acts of kindness examples, kindness around the world, share your kindness story, human kindness, pay it forward stories, global kindness, real kindness stories, heartwarming stories, stories of generosity, community kindness, kind people stories, moments of kindness, kindness project, spread kindness, inspiring acts of kindness, everyday heroes",
  url: "https://connectco.app/kind-moments",
  canonical: "https://connectco.app/kind-moments",
  robots: "index, follow",
  openGraph: {
    title: "Kind Moments — Stories of Human Kindness from Around the World | Connect & Co",
    description:
      "Real stories. Real people. Every country welcome. Share the moment someone's kindness stopped you in your tracks.",
    url: "https://connectco.app/kind-moments",
    image: "https://connectco.app/kind-moments-og.jpg",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US",
  },
  twitter: {
    card: "summary_large_image",
    title: "Kind Moments — Stories of Human Kindness from Around the World",
    description:
      "Real stories. Every country welcome. Share yours at connectco.app/kind-moments",
    image: "https://connectco.app/kind-moments-og.jpg",
  },
  additionalMeta: [
    { property: "og:image:width",  content: "1200" },
    { property: "og:image:height", content: "630"  },
    { property: "og:image:alt",    content: "Kind Moments — a globe surrounded by warm light and human figures sharing coffee and stories" },
    { name: "author",    content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
  ],
},


happiness: {
  title: "How to Be Happy in Tokyo: Authentic Friendships & Fika | Connect & Co",
  description: "Discover how authentic friendships and curated community events create happiness. Join Connect & Co for Fika, curated connections, and growth circles in Tokyo.",
  keywords: "happiness tokyo, how to be happy, friendship happiness, authentic connection, fika tradition, community events tokyo, meaningful relationships, swedish fika happiness, social connection wellbeing, authentic friendships tokyo, community happiness, loneliness prevention tokyo, social wellbeing japan, international community happiness, friendship research tokyo",
  url: "https://connectco.app/happiness",
  canonical: "https://connectco.app/happiness",
  robots: "index, follow",
  openGraph: {
    title: "How to Be Happy in Tokyo: Authentic Friendships & Fika",
    description: "Discover how authentic friendships and curated community events create happiness through Swedish Fika tradition and meaningful connections.",
    url: "https://connectco.app/happiness",
    image: "https://connectco.app/happiness-og.jpg",
    imageAlt: "Happy people connecting over Swedish Fika coffee tradition in Tokyo",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "How to Be Happy in Tokyo: Authentic Friendships & Fika",
    description: "Discover how authentic friendships create happiness through Swedish Fika and meaningful connections.",
    image: "https://connectco.app/happiness-og.jpg",
    imageAlt: "Connect & Co happiness through friendship and Swedish Fika tradition"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "theme-color", content: "#f59e0b" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "target-audience", content: "International professionals seeking happiness through authentic friendships" },
    { name: "geographic-coverage", content: "Tokyo, Japan and Global" },
    { name: "content-category", content: "Happiness, Friendship, Community Building" }
  ]
},
  
  events: {
    title: " International Fika Community Building Hub- Swedish Fika, Forest Walks & More | Connect & Co",
    description: "Join waitlists for our most popular events: Swedish-Inspired Fika, Dawn & Zen Forest Walks, Shoot the Breeze Happy Hour. Members get priority access.",
    keywords: "Swedish fika, forest walks, happy hour networking, themed discussions, exclusive events, priority access, community building through structured dialogue",
    url: "https://connectco.app/events",
    canonical: "https://connectco.app/events",
    openGraph: {
      title: "Exclusive Networking Events - Swedish Fika, Forest Walks & More",
      description: "Join waitlists for our most popular events: Swedish-Inspired Fika, Dawn & Zen Forest Walks, Shoot the Breeze Happy Hour.",
      url: "https://connectco.app/events",
      image: "https://connectco.app/events-og.jpg",
      type: "website"
    },
    twitter: {
      card: "summary_large_image",
      title: "Exclusive Networking Events - Swedish Fika, Forest Walks & More",
      description: "Join waitlists for our most popular events: Swedish-Inspired Fika, Dawn & Zen Forest Walks, Shoot the Breeze Happy Hour.",
      image: "https://connectco.app/events-og.jpg"
    }
  },

  whyConnectCo: {
  title: "Why Connect & Co: Tokyo's Curated Friendship Community | Built Different",
  description: "Connect & Co is Tokyo's membership-based fika community for locals, expats, and digital nomads. See how we compare to Meetup, Eventbrite, Bumble BFF, and InterNations — and why curated intimacy builds real friendships.",
  keywords: "connect and co tokyo, fika community tokyo, meetup alternative tokyo, internations alternative, bumble bff alternative, curated friendship events tokyo, expat community tokyo, small group social events, authentic connection tokyo, swedish fika tokyo, social apps tokyo, real friendships tokyo, why connect and co, friendship not networking",
  url: "https://connectco.app/why-connectco",
  canonical: "https://connectco.app/why-connectco",
  robots: "index, follow",
  openGraph: {
    title: "Why Connect & Co: Tokyo's Curated Friendship Community",
    description: "No networking. No agenda. No strangers — just people who haven't met yet. See how Connect & Co compares to every other social platform in Tokyo.",
    url: "https://connectco.app/why-connectco",
    image: "https://connectco.app/why-connectco-og.jpg",
    imageAlt: "Connect & Co positioning map comparing fika community to Meetup, Eventbrite, Bumble BFF and InterNations",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Why Connect & Co: Tokyo's Curated Friendship Community",
    description: "No networking. No agenda. No strangers — just people who haven't met yet.",
    image: "https://connectco.app/why-connectco-og.jpg",
    imageAlt: "Connect & Co vs Meetup, Eventbrite, Bumble BFF and InterNations positioning map"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "theme-color", content: "#3B5E4A" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { name: "target-audience", content: "Locals, expats, and digital nomads in Tokyo seeking real friendships" },
    { name: "geographic-coverage", content: "Tokyo, Japan" },
    { name: "content-category", content: "Community Platform Comparison, Friendship Building" }
  ]
},

  partnerWithUs: {
  title: "Partner with ConnectCo | Reach Tokyo Expats & International Professionals",
  description: "Sponsor ConnectCo events and reach 450+ English-speaking expats, digital nomads, and international professionals in Tokyo. Newsletter placements, event sponsorships, and presenting partnerships available. Post-event reports included.",
  keywords: "sponsor tokyo expats, tokyo expat marketing, international professional tokyo sponsorship, connectco partnership, tokyo community sponsorship, reach expats tokyo, english speaking professionals tokyo, fika event sponsor, tokyo brand partnership, expat audience tokyo, digital nomad marketing tokyo, international community sponsor japan",
  url: "https://connectco.app/partner-with-us",
  canonical: "https://connectco.app/partner-with-us",
  robots: "index, follow",
  openGraph: {
    title: "Partner with ConnectCo | Reach Tokyo Expats & International Professionals",
    description: "Reach 450+ English-speaking expats and international professionals in Tokyo through in-person event sponsorships, newsletter placements, and community partnerships.",
    url: "https://connectco.app/partner-with-us",
    image: "https://connectco.app/partner-og.jpg",
    imageAlt: "Partner with ConnectCo — Tokyo expat community sponsorship opportunities",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Partner with ConnectCo | Reach Tokyo Expats & International Professionals",
    description: "Reach 450+ English-speaking expats and international professionals in Tokyo through curated in-person event sponsorships.",
    image: "https://connectco.app/partner-twitter.jpg",
    imageAlt: "Partner with ConnectCo — Tokyo expat community sponsorship"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "theme-color", content: "#3B5E4A" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/jpeg" },
    { name: "target-audience", content: "Brands, marketers, and businesses targeting English-speaking expats in Tokyo" },
    { name: "geographic-coverage", content: "Tokyo, Japan" },
    { name: "content-category", content: "Brand Partnership, Sponsorship, Community Marketing" },
    { name: "document-type", content: "Partnership Page" }
  ]
},
  
  membership: {
    title: "Premium Membership Benefits - 24hr Early Access & Member Directory | Connect & Co",
    description: "Get 24hr early access to events, priority waitlist placement, member directory access, exclusive photos, and members-only gatherings with 25-30 people.",
    keywords: "premium membership, early access, member directory, exclusive events, priority waitlist, networking benefits",
    url: "https://connectco.app/membership",
    canonical: "https://connectco.app/membership",
    openGraph: {
      title: "Premium Membership Benefits - 24hr Early Access & Member Directory",
      description: "Get 24hr early access to events, priority waitlist placement, member directory access, exclusive photos, and members-only gatherings.",
      url: "https://connectco.app/membership",
      image: "https://connectco.app/membership-og.jpg",
      type: "website"
    },
    twitter: {
      card: "summary_large_image",
      title: "Premium Membership Benefits - 24hr Early Access & Member Directory",
      description: "Get 24hr early access to events, priority waitlist placement, member directory access, exclusive photos.",
      image: "https://connectco.app/membership-og.jpg"
    }
  },
  
  blog: {
    title: " International Community Building Hub Tips, Member Stories & Cultural Insights | Connect & Co",
    description: "Insider tips for networking worldwide, inspiring member success stories, and cultural insights for international professionals. Coffee shop guides, networking strategies & more.",
    keywords: "networking blog, city tips, member stories, cultural insights, international networking, coffee shops, startup founders, community building through structured dialogue",
    url: "https://connectco.app/blog",
    canonical: "https://connectco.app/blog",
    openGraph: {
      title: " International Community Building Hub Tips, Member Stories & Cultural Insights",
      description: "Insider tips for networking worldwide, inspiring member success stories, and cultural insights for international professionals.",
      url: "https://connectco.app/blog",
      image: "https://connectco.app/blog-og.jpg",
      type: "website"
    },
    twitter: {
      card: "summary_large_image",
      title: " International Community Building Hub - City Tips, Member Stories & Cultural Insights",
      description: "Insider tips for networking worldwide, inspiring member success stories, and cultural insights for international professionals.",
      image: "https://connectco.app/blog-og.jpg"
    }
  },

  monthlySwedishFika: {
    title: "Monthly Swedish Fika in Tokyo | Free Community Coffee Tradition | Connect & Co",
    description: "Join Tokyo's beloved monthly Swedish Fika tradition. Open to all, authentic coffee culture, meaningful conversations. First Sunday each month in premium third-wave cafés.",
    keywords: "monthly fika tokyo, swedish fika events, free coffee meetup tokyo, authentic fika experience, international community tokyo, scandinavian culture tokyo, coffee tradition, community gathering, community building through structured dialogue",
    url: "https://connectco.app/monthly-fika",
    canonical: "https://connectco.app/monthly-fika",
    openGraph: {
      title: "Monthly Swedish Fika in Tokyo | Free Community Coffee Tradition",
      description: "Join Tokyo's beloved monthly Swedish Fika tradition. Open to all, authentic coffee culture, meaningful conversations.",
      url: "https://connectco.app/monthly-fika",
      image: "https://connectco.app/monthly-fika-og.jpg",
      type: "website"
    },
    twitter: {
      card: "summary_large_image",
      title: "Monthly Swedish Fika in Tokyo | Free Community Coffee Tradition",
      description: "Join Tokyo's beloved monthly Swedish Fika tradition. Open to all, authentic coffee culture, meaningful conversations.",
      image: "https://connectco.app/monthly-fika-og.jpg"
    }
  },

  whatIsFika: {
    title: "What is Fika? Understanding Sweden's Art of Slow Connection | Connect & Co",
    description: "Discover the Swedish tradition of fika - more than just coffee, it's a practice of slowing down, connecting authentically, and building meaningful relationships. Learn how this cultural ritual transforms modern community building.",
    keywords: "what is fika, swedish fika tradition, fika meaning, swedish coffee culture, slow living, authentic connection, community building, mindful coffee, swedish culture, intentional socializing, community building through structured dialogue",
    url: "https://connectco.app/what-is-fika",
    canonical: "https://connectco.app/what-is-fika",
    openGraph: {
      title: "What is Fika? Understanding Sweden's Art of Slow Connection",
      description: "Discover the Swedish tradition of fika - more than just coffee, it's a practice of slowing down, connecting authentically, and building meaningful relationships.",
      url: "https://connectco.app/what-is-fika",
      image: "https://connectco.app/what-is-fika-og.jpg",
      type: "website"
    },
    twitter: {
      card: "summary_large_image",
      title: "What is Fika? Understanding Sweden's Art of Slow Connection",
      description: "Discover the Swedish tradition of fika - more than just coffee, it's a practice of slowing down and connecting authentically.",
      image: "https://connectco.app/what-is-fika-og.jpg"
    }
  },

    // NEW: Signature Fika SEO Configuration
    signatureFika: {
      title: "Signature Weekly Fika - Premium Weekly Swedish Coffee Tradition | Connect & Co",
      description: "Join our exclusive weekly Signature Fika every Tuesday. Premium venues, 20-30 exceptional people, authentic Swedish tradition. Your predictable place for unpredictable conversations.",
      keywords: "signature fika, weekly fika, premium fika tokyo, exclusive fika, swedish coffee tradition, tuesday fika, premium community events, weekly networking, authentic fika experience, social infrastructure, community building through structured dialogue",
      url: "https://connectco.app/signature-fika",
      canonical: "https://connectco.app/signature-fika",
      openGraph: {
        title: "Signature Weekly Fika - Premium Weekly Swedish Coffee Tradition",
        description: "Join our exclusive weekly Signature Fika every Tuesday. Premium venues, authentic conversations, exceptional people. Your social infrastructure in Tokyo.",
        url: "https://connectco.app/signature-fika",
        image: "https://connectco.app/signature-fika-og.jpg",
        type: "website"
      },
      twitter: {
        card: "summary_large_image",
        title: "Signature Weekly Fika - Premium Weekly Swedish Coffee Tradition",
        description: "Weekly Swedish Fika tradition. Premium venues, authentic conversations, exceptional people every Tuesday.",
        image: "https://connectco.app/signature-fika-og.jpg"
      }
    },

    // ADD THESE TO THE seoConfigs OBJECT:

whatIsCuratedFika: {
  title: "What is Curated Fika? AI-Assisted Intimate Swedish Coffee Tradition | Connect & Co",
  description: "Discover Curated Fika - the most intimate expression of Swedish tradition. AI-assisted expert curation matches 3-4 compatible individuals for meaningful 90-minute conversations at premium venues. Quality over quantity connection.",
  keywords: "what is curated fika, curated fika meaning, community building, AI-assisted fika, intimate fika experience, premium fika, swedish fika curation, small group fika, quality networking, authentic connection, intimate swedish tradition, premium coffee experience, expert matching fika, 3-4 person fika, AI networking, curated networking events, community building through curation",
  url: "https://connectco.app/what-is-curated-fika",
  canonical: "https://connectco.app/what-is-curated-fika",
  openGraph: {
    title: "What is Curated Fika? AI-Assisted Intimate Swedish Coffee Tradition",
    description: "Discover the most intimate expression of Swedish fika tradition. AI-assisted expert curation matches 3-4 compatible individuals for meaningful conversations at premium venues.",
    url: "https://connectco.app/what-is-curated-fika",
    image: "https://connectco.app/what-is-curated-fika-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "What is Curated Fika? AI-Assisted Intimate Swedish Coffee Tradition",
    description: "The most intimate expression of Swedish fika tradition with AI-assisted expert curation for 3-4 compatible individuals.",
    image: "https://connectco.app/what-is-curated-fika-og.jpg"
  }
},

whatIsSpeedFika: {
  title: "What is Speed Fika? Psychology-Based Deep Connection Events | Connect & Co",
  description: "Discover Speed Fika Connection - revolutionary approach using 12 psychology-based questions across 4 rotating rounds. Build authentic relationships through structured meaningful dialogue, not speed dating.",
  keywords: "what is speed fika, speed fika meaning, community building, speed fika connection, psychology-based questions, structured conversations, deep connection events, rotating fika, 12 questions fika, meaningful dialogue, authentic relationship building, group fika rotation, connection psychology, structured networking, speed networking alternative, authentic speed networking, community building through structured dialogue",
  url: "https://connectco.app/what-is-speed-fika",
  canonical: "https://connectco.app/what-is-speed-fika",
  openGraph: {
    title: "What is Speed Fika? Psychology-Based Deep Connection Events",
    description: "Revolutionary approach using 12 psychology-based questions across 4 rotating rounds. Build authentic relationships through structured meaningful dialogue.",
    url: "https://connectco.app/what-is-speed-fika",
    image: "https://connectco.app/what-is-speed-fika-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "What is Speed Fika? Psychology-Based Deep Connection Events",
    description: "Revolutionary Speed Fika using 12 psychology-based questions across 4 rotating rounds for authentic relationship building.",
    image: "https://connectco.app/what-is-speed-fika-og.jpg"
  }
},

  // Add these to your seoConfigs object in SEO.jsx
curatedFika: {
  title: "Curated Fika - Ultra-Premium Intimate Swedish Coffee Experience | Connect & Co",
  description: "Discover Curated Fika: Tokyo's most exclusive Swedish coffee tradition. 3-4 personally matched participants in premium venues. The pinnacle of authentic connection through intimate frienships, thoughtfully curated gatherings.",
  keywords: "curated fika, ultra premium fika, intimate networking tokyo, exclusive swedish coffee, authentic frienships, personal matching service, premium small group meetup, authentic connection, high-end networking, boutique community events, selective networking, tokyo premium experiences",
  url: "https://connectco.app/curated-fika",
  canonical: "https://connectco.app/curated-fika",
  openGraph: {
    title: "Curated Fika - Ultra-Premium Intimate Swedish Coffee Experience",
    description: "Tokyo's most exclusive Swedish coffee tradition. 3-4 personally matched participants in premium venues for authentic, meaningful connections.",
    url: "https://connectco.app/curated-fika",
    image: "https://connectco.app/curated-fika-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "Curated Fika - Ultra-Premium Intimate Swedish Coffee Experience",
    description: "Tokyo's most exclusive Swedish coffee tradition. 3-4 personally matched participants in premium venues.",
    image: "https://connectco.app/curated-fika-og.jpg"
  }
},

curatedFikaSignup: {
  title: "Apply for Curated Fika - Premium Intimate Swedish Coffee Experience | Connect & Co",
  description: "Apply for Curated Fika - exclusive 3-4 person Swedish coffee gatherings in Tokyo. Premium intimate frienships with personally matched professionals. Applications now open.",
  keywords: "curated fika application, premium fika tokyo, authentic friendships, intimate networking, swedish coffee experience, small group meetup, premium membership, tokyo networking, professional matching, authentic connections",
  url: "https://connectco.app/curated-fika-signup",
  canonical: "https://connectco.app/curated-fika-signup",
  openGraph: {
    title: "Apply for Curated Fika - Premium Intimate Swedish Coffee Experience",
    description: "Apply for exclusive 3-4 person Swedish coffee gatherings in Tokyo. Premium intimate networking with personally matched professionals.",
    url: "https://connectco.app/curated-fika-signup",
    image: "https://connectco.app/curated-fika-signup-og.jpg",
    type: "website"
  },
  twitter: {
    card: "summary_large_image",
    title: "Apply for Curated Fika - Premium Intimate Swedish Coffee Experience",
    description: "Apply for exclusive 3-4 person Swedish coffee gatherings in Tokyo. Premium networking with personally matched friendships.",
    image: "https://connectco.app/curated-fika-signup-og.jpg"
  }
},


curatedFikaEntrepreneursSignup: {
  title: "Apply for Founders 1-on-1 | Confidential Matchmaking for Entrepreneurs | Connect & Co",
  description: "Apply for exclusive 1-on-1 Curated Fika sessions with fellow founders, entrepreneurs, and operators in Tokyo. Confidential, high-trust matchmaking focused on real business challenges. AI-assisted curation for meaningful connections.",
  keywords: "founder application, entrepreneur 1-on-1 application, startup founder matching tokyo, confidential founder meetings, entrepreneur networking application, founder fika signup, 1-on-1 entrepreneur sessions, tokyo founder community application, startup operator matching, tech entrepreneur networking tokyo, founder peer support application, confidential business discussions, entrepreneur matching service, founder accountability partner",
  url: "https://connectco.app/curated-fika/founders-1on1/apply",
  canonical: "https://connectco.app/curated-fika/founders-1on1/apply",
  robots: "index, follow",
  openGraph: {
    title: "Apply for Founders 1-on-1 | Confidential Matchmaking for Entrepreneurs",
    description: "Apply for exclusive 1-on-1 sessions with fellow founders and operators. Confidential, high-trust matchmaking focused on real business challenges in Tokyo.",
    url: "https://connectco.app/curated-fika/founders-1on1/apply",
    image: "https://connectco.app/curated-fika-founders-apply-og.jpg",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Apply for Founders 1-on-1 | Confidential Matchmaking for Entrepreneurs",
    description: "Exclusive 1-on-1 sessions with fellow founders. Confidential, high-trust matchmaking focused on real business challenges.",
    image: "https://connectco.app/curated-fika-founders-apply-twitter.jpg"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "application-name", content: "Founders 1-on-1 Application" },
    { name: "apple-mobile-web-app-title", content: "Founders 1-on-1 Apply" },
    { name: "theme-color", content: "#7c3aed" },
    { name: "format-detection", content: "telephone=no" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/jpeg" },
    { property: "og:image:alt", content: "Founders 1-on-1 application form for confidential entrepreneur matchmaking" },
    { name: "target-audience", content: "Founders, entrepreneurs, solopreneurs, startup operators, tech builders" },
    { name: "geographic-coverage", content: "Tokyo, Japan" },
    { name: "content-category", content: "Application Form, Entrepreneur Networking" }
  ]
},


wellness: {
  title: "Community Wellness Hub - Mental Health Support for International Networking | Connect & Co",
  description: "Supporting your mental health journey in shared experiences. Get pre-event mood check-ins, social anxiety resources, mindful networking tips, and peer support for authentic connections.",
  keywords: "mental health networking, social anxiety support, mindful networking, community wellness, community building",
  url: "https://connectco.app/wellness",
  canonical: "https://connectco.app/wellness"
},




    // ENHANCED: Goal Accountability Partners SEO with comprehensive optimization
goalAccountabilityPartners: {
  title: "Goal Accountability Partners - Find Your Accountability Tribe | Transform Goals Into Success | Connect & Co",
  description: "Join 6-8 like-minded people pursuing the same goal through authentic Swedish fika tradition. Turn solo struggles into shared success with weekly check-ins, expert matching, and proven accountability systems. Free 6-week program with global community support.",
  keywords: "goal accountability partners, accountability tribe, goal achievement, personal development community support, fika tradition accountability, authentic accountability groups, goal achievement support system, accountability partner matching, habit formation support, goal tracking community, peer accountability, accountability buddy system, goal success coaching, monthly goal tracking, weekly accountability check-ins, accountability partner program, goal achievement strategy, habit accountability, personal growth accountability, professional development goals, fitness goal accountability, language learning accountability, reading challenge accountability, creative project accountability, entrepreneurial goal support, skill mastery accountability, academic study support, goal setting community, achievement tracking system, accountability group dynamics, goal completion support, sustainable habit formation, goal motivation system",
  url: "https://connectco.app/goal-accountability-partners",
  canonical: "https://connectco.app/goal-accountability-partners",
  robots: "index, follow",
  openGraph: {
    title: "Goal Accountability Partners - Find Your Accountability Tribe | Transform Goals Into Success",
    description: "Join 6-8 like-minded people pursuing the same goal through authentic Swedish fika tradition. Turn solo struggles into shared success with weekly check-ins and expert matching.",
    url: "https://connectco.app/goal-accountability-partners",
    image: "https://connectco.app/goal-accountability-partners-og.jpg",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Goal Accountability Partners - Find Your Accountability Tribe",
    description: "Join authentic accountability groups in the Swedish fika tradition. Transform your goals into shared success with 6-8 like-minded people.",
    image: "https://connectco.app/goal-accountability-partners-og.jpg"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "application-name", content: "Connect & Co Goal Accountability Partners" },
    { name: "apple-mobile-web-app-title", content: "Goal Accountability Partners" },
    { name: "theme-color", content: "#8b5cf6" },
    { name: "format-detection", content: "telephone=no" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:alt", content: "Goal Accountability Partners - Swedish fika tradition meets goal achievement" }
  ]
},

  // 🆕 NEW: Conversation Starters SEO Configuration
  conversationStarters: {
    title: "Fika Conversation Starters & Cultural Learning Hub - 300+ Questions for Meaningful Connection | Connect & Co",
    description: "Master the art of meaningful Fika conversation with 300+ thoughtful questions across 10 categories. Features cultural learning from Japan, Sweden, Denmark & more. Perfect for networking, expat life & building fika authentic connections.",
    keywords: "fika, fika conversation starters, networking questions, cultural conversation topics, expat conversation starters, meaningful conversation questions, community building, tokyo community, japan communites, cultural learning, networking tips, international community conversation, tokyo networking questions, community building, authentic fika connection questions",
    url: "https://connectco.app/conversation-starters",
    canonical: "https://connectco.app/conversation-starters",
    openGraph: {
      title: "300+ Fika Conversation Starters & Cultural Learning Hub - Meaningful Connection Made Easy",
      description: "Master meaningful Fika conversation with 200+ thoughtful questions across 10 categories. Features cultural concepts from Japan, Sweden, Denmark & more for authentic connections.",
      url: "https://connectco.app/conversation-starters",
      image: "https://connectco.app/conversation-starters-og.jpg",
      type: "website"
    },
    twitter: {
      card: "summary_large_image",
      title: "300+ Fika Conversation Starters & Cultural Learning Hub",
      description: "Master meaningful Fika conversation with thoughtful questions across 10 categories. Cultural learning from Japan, Sweden, Denmark & more.",
      image: "https://connectco.app/conversation-starters-og.jpg"
    }
  },


   // 🆕 NEW: Tokyo Expat Guide SEO Configuration
  tokyoExpatGuide: {
    title: "Complete Tokyo Expat Guide - Essential Community & Survival Tips | Connect & Co",
    description: "Essential guide for expats moving to Tokyo. Join 2,500+ international professionals who've made Tokyo home through authentic community connections. Skip the lonely expat phase with Swedish fika traditions, practical survival tips, and instant community access.",
    keywords: "tokyo expat guide, moving to tokyo, expat community japan, international professionals tokyo, tokyo newcomers, expat networking tokyo, tokyo survival guide, expat social community, international community tokyo, tokyo expat resources, expat friends tokyo, expat life japan",
    url: "https://connectco.app/tokyo-expat-guide",
    canonical: "https://connectco.app/tokyo-expat-guide",
    openGraph: {
      title: "Complete Tokyo Expat Guide - Essential Community & Survival Tips",
      description: "Skip the lonely expat phase. Join 2,500+ international professionals who've made Tokyo home through authentic community connections and Swedish fika traditions.",
      url: "https://connectco.app/tokyo-expat-guide",
      image: "https://connectco.app/tokyo-expat-guide-og.jpg",
      type: "website"
    },
    twitter: {
      card: "summary_large_image",
      title: "Complete Tokyo Expat Guide - Essential Community & Survival Tips",
      description: "Essential guide for expats moving to Tokyo. Join our authentic community and skip the lonely expat phase.",
      image: "https://connectco.app/tokyo-expat-guide-og.jpg"
    }
  },

  tokyoSurvivalGuide: {
    title: "Free Tokyo Survival Guide 2026 - Complete Expat Handbook | Banking, Housing, Transportation & More | Connect & Co",
    description: "Download your free comprehensive Tokyo survival guide written by expats. Covers banking setup, housing search, transportation, healthcare, cultural integration & emergency resources. 50+ practical tips for thriving in Tokyo from day one.",
    keywords: "tokyo survival guide, tokyo expat guide, moving to tokyo, tokyo newcomer guide, japan expat resources, tokyo living guide, tokyo banking guide, tokyo housing guide, tokyo transportation guide, expat survival tokyo, international tokyo guide, japan survival tips, tokyo practical guide, expat handbook japan, tokyo newcomer resources, japan living guide, international professionals tokyo, tokyo expat survival, tokyo move checklist, japan practical tips, tokyo setup guide, expat tokyo resources, living in tokyo guide, tokyo essential guide, tokyo startup guide",
    url: "https://connectco.app/tokyo-survival-guide",
    canonical: "https://connectco.app/tokyo-survival-guide",
    robots: "index, follow",
    openGraph: {
      title: "Free Tokyo Survival Guide 2026 - Complete Expat Handbook | 50+ Essential Tips",
      description: "Download your free comprehensive Tokyo survival guide written by expats. Banking, housing, transportation, healthcare & cultural integration. Everything you need to thrive in Tokyo from day one.",
      url: "https://connectco.app/tokyo-survival-guide",
      image: "https://connectco.app/tokyo-survival-guide-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: "Free Tokyo Survival Guide 2026 - Complete Expat Handbook",
      description: "Download your free comprehensive Tokyo survival guide written by expats. 50+ essential tips covering banking, housing, transportation & more.",
      image: "https://connectco.app/tokyo-survival-guide-og.jpg"
    },
    additionalMeta: [
      { name: "author", content: "Connect & Co Expat Community" },
      { name: "publisher", content: "Connect & Co" },
      { name: "application-name", content: "Tokyo Survival Guide" },
      { name: "apple-mobile-web-app-title", content: "Tokyo Survival Guide" },
      { name: "theme-color", content: "#10b981" },
      { name: "format-detection", content: "telephone=no" },
      { property: "article:author", content: "Connect & Co" },
      { property: "article:publisher", content: "https://connectco.app" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Tokyo Survival Guide 2026 - Complete handbook for expats with practical tips" },
      { name: "document-classification", content: "Guide" },
      { name: "document-type", content: "Survival Guide" },
      { name: "coverage", content: "Tokyo, Japan" },
      { name: "target-audience", content: "International expats, digital nomads, professionals" }
    ]
  },
  
  tokyoExpatCalculator: {
    title: "Free Tokyo Expat Integration Calculator - Cost, Time & Loneliness Analysis | Connect & Co",
    description: "Calculate how much time, money and loneliness you can save choosing authentic community over traditional networking. Free tool used by 2,800+ Tokyo expats.",
    keywords: "tokyo expat calculator, expat integration cost, tokyo networking analysis, expat friendship timeline, tokyo social budget calculator",
    url: "https://connectco.app/tokyo-expat-calculator",
    canonical: "https://connectco.app/tokyo-expat-calculator",
    openGraph: {
      title: "Free Tokyo Expat Integration Calculator - Cost, Time & Loneliness Analysis",
      description: "Calculate how much time, money and loneliness you can save choosing authentic community over traditional networking. Used by 2,800+ Tokyo expats.",
      url: "https://connectco.app/tokyo-expat-calculator",
      image: "https://connectco.app/tokyo-expat-calculator-og.jpg",
      imageAlt: "Tokyo Expat Calculator - Compare integration strategies with cost and time analysis",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: "Free Tokyo Expat Integration Calculator - Cost, Time & Loneliness Analysis",
      description: "Calculate how much time, money and loneliness you can save choosing authentic community over traditional networking. Free tool for Tokyo expats.",
      image: "https://connectco.app/tokyo-expat-calculator-twitter.jpg",
      imageAlt: "Tokyo Expat Calculator showing cost savings and integration timeline analysis"
    },
    // ADD STRUCTURED DATA HERE
    structuredData: [
      {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "Tokyo Expat Integration Calculator",
        "description": "Free calculator tool that helps international expats in Tokyo compare integration strategies and analyze costs",
        "url": "https://connectco.app/tokyo-expat-calculator",
        "applicationCategory": "UtilityApplication",
        "operatingSystem": "Web Browser",
        "isAccessibleForFree": true,
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "author": {
          "@type": "Organization",
          "name": "Connect & Co",
          "url": "https://connectco.app"
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How accurate is the Tokyo Expat Integration Calculator?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "The calculator is based on data from 2,800+ expats in Tokyo and factors in cultural background, Japanese language level, budget, and personality type."
            }
          },
          {
            "@type": "Question",
            "name": "Is the Tokyo Expat Calculator really free?",
            "acceptedAnswer": {
              "@type": "Answer", 
              "text": "Yes, completely free! No email required to use the calculator. The core calculator and results are always free to use and share."
            }
          }
        ]
      }
    ]
  },

  // Privacy Policy page
  privacy: {
    title: "Privacy Policy | Connect & Co",
    description: "Learn how Connect & Co protects your privacy and handles your personal information in our community platform.",
    keywords: "privacy policy, data protection, personal information, Connect & Co",
    url: "https://connectco.app/privacy",
    canonical: "https://connectco.app/privacy",
    openGraph: {
      title: "Privacy Policy | Connect & Co",
      description: "Learn how Connect & Co protects your privacy and handles your personal information.",
      url: "https://connectco.app/privacy",
      image: "https://connectco.app/og-image.jpg",
      type: "website"
    }
  },



  // Terms of Service page
  terms: {
    title: "Terms of Service | Connect & Co",
    description: "Terms and conditions for using Connect & Co's community platform and attending our networking events.",
    keywords: "terms of service, terms and conditions, user agreement, Connect & Co",
    url: "https://connectco.app/terms",
    canonical: "https://connectco.app/terms",
    openGraph: {
      title: "Terms of Service | Connect & Co",
      description: "Terms and conditions for using Connect & Co's community platform and attending our networking events.",
      url: "https://connectco.app/terms",
      image: "https://connectco.app/og-image.jpg",
      type: "website"
    }
  },

  community: {
    title: "The Fika Board — Community Conversations | Connect & Co",
    description:
      "Where the conversation continues between gatherings. Ask questions, share reflections, post discoveries, and keep the connection alive. The Connect & Co community board for locals, expats, and digital nomads in Tokyo and beyond.",
    keywords:
      "connect and co community, fika board, tokyo expat community, digital nomad community tokyo, expat conversations tokyo, fika community discussions, tokyo friendship forum, community board tokyo, connect co members, fika conversations, tokyo locals community, expat forum japan, community reflections, tokyo community questions",
    url: "https://connectco.app/community",
    canonical: "https://connectco.app/community",
    robots: "index, follow",
    openGraph: {
      title: "The Fika Board — Community Conversations | Connect & Co",
      description:
        "Ask questions, share reflections, post discoveries. The Connect & Co community board — where the conversation continues between gatherings.",
      url: "https://connectco.app/community",
      image: "https://connectco.app/community-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US",
    },
    twitter: {
      card: "summary_large_image",
      title: "The Fika Board — Community Conversations | Connect & Co",
      description:
        "Where the conversation continues between gatherings. Join the Connect & Co community board at connectco.app/community",
      image: "https://connectco.app/community-og.jpg",
    },
    additionalMeta: [
      { property: "og:image:width",  content: "1200" },
      { property: "og:image:height", content: "630"  },
      { property: "og:image:alt",    content: "The Fika Board — a warm community space for honest conversations over coffee" },
      { name: "author",    content: "Connect & Co" },
      { name: "publisher", content: "Connect & Co" },
    ],
  },

  communityPost: {
    title: "Community Post | The Fika Board | Connect & Co",
    description:
      "A conversation on the Connect & Co community board. Join the discussion, share a reflection, or reply with your own perspective.",
    keywords:
      "connect and co community, fika board post, tokyo expat community, community discussion, fika conversation",
    url: "https://connectco.app/community",
    canonical: "https://connectco.app/community",
    robots: "index, follow",
    openGraph: {
      title: "Community Post | The Fika Board | Connect & Co",
      description:
        "A conversation on the Connect & Co community board. Join the discussion at connectco.app/community",
      url: "https://connectco.app/community",
      image: "https://connectco.app/community-og.jpg",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US",
    },
    twitter: {
      card: "summary_large_image",
      title: "Community Post | The Fika Board | Connect & Co",
      description:
        "A conversation on the Connect & Co community board. Join the discussion at connectco.app/community",
      image: "https://connectco.app/community-og.jpg",
    },
    additionalMeta: [
      { property: "og:image:width",  content: "1200" },
      { property: "og:image:height", content: "630"  },
      { property: "og:image:alt",    content: "The Fika Board — a warm community space for honest conversations over coffee" },
      { name: "author",    content: "Connect & Co" },
      { name: "publisher", content: "Connect & Co" },
    ],
  },

  meetPeopleTokyo: {
    title: "Meet People in Tokyo | ConnectCo — Fika Community",
    description: "Feel lonely in Tokyo? ConnectCo brings locals, expats & digital nomads together over coffee. No networking. Real friendships. Monthly fika, weekly Wednesday gatherings, curated small groups. Featured in Metropolis Japan.",
    keywords: "meet people tokyo, make friends tokyo, tokyo expat community, lonely in tokyo, social events tokyo, english speaking events tokyo, tokyo community, fika tokyo, expat social tokyo, weekly events tokyo, coffee events tokyo, english community tokyo, tokyo community for foreigners",
    url: "https://connectco.app/meet-people-tokyo",
    canonical: "https://connectco.app/meet-people-tokyo",
    robots: "index, follow",
    openGraph: {
      title: "Meet People in Tokyo | ConnectCo",
      description: "Feel lonely in Tokyo? ConnectCo brings locals, expats & digital nomads together over coffee. No networking. Real friendships. Events sell out every month.",
      url: "https://connectco.app/meet-people-tokyo",
      image: "https://connectco.app/og-image.jpg",
      imageAlt: "ConnectCo community gathering over coffee in Tokyo",
      type: "website",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      title: "Meet People in Tokyo | ConnectCo",
      description: "Feel lonely in Tokyo? ConnectCo brings locals, expats & digital nomads together over coffee. Real friendships, no networking.",
      image: "https://connectco.app/og-image.jpg"
    },
    additionalMeta: [
      { property: "og:image:width",  content: "1200" },
      { property: "og:image:height", content: "630"  },
      { name: "author",    content: "Connect & Co" },
      { name: "publisher", content: "Connect & Co" },
      { name: "geo.region",    content: "JP-13" },
      { name: "geo.placename", content: "Tokyo" },
    ],
  },
};



// Function to create dynamic canonical URLs
export const createCanonicalUrl = (path, baseUrl = "https://connectco.app") => {
  // Remove trailing slashes and clean the path
  const cleanPath = path.replace(/\/+$/, '').replace(/^\/+/, '');
  return cleanPath ? `${baseUrl}/${cleanPath}` : baseUrl;
};


export const createBlogPostSEO = (post) => ({
  title: `${post.title} | Connect & Co Blog`,
  description: post.excerpt || post.description,
  keywords: post.tags ? post.tags.join(', ') : 'networking, blog, community',
  url: `https://connectco.app/blog/${post.slug}`,
  canonical: `https://connectco.app/blog/${post.slug}`,
  openGraph: {
    title: post.title,
    description: post.excerpt || post.description,
    url: `https://connectco.app/blog/${post.slug}`,
    image: post.featuredImage || `https://connectco.app/blog-images/${post.slug}.jpg`,
    type: "article",
    article: {
      publishedTime: post.publishedAt,
      modifiedTime: post.updatedAt,
      author: post.author,
      section: "Networking",
      tags: post.tags
    }
  },
  twitter: {
    card: "summary_large_image",
    title: post.title,
    description: post.excerpt || post.description,
    image: post.featuredImage || `https://connectco.app/blog-images/${post.slug}.jpg`
  }
});

// Function to create blog post SEO config
export const createBlogPostSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "headline": post.title,
  "description": post.excerpt,
  "image": `https://connectco.app/images/blog/${post.image}`,
  "datePublished": post.date,
  "dateModified": post.updatedAt || post.date,
  "author": {
    "@type": "Person",
    "name": post.author,
    "url": "https://connectco.app/about"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png"
    }
  },
  "mainEntityOfPage": {
    "@type": "WebPage",
    "@id": `https://connectco.app/blog/${post.slug}`
  },
  "keywords": post.tags?.join(", ") || "",
  "articleSection": post.category,
  "wordCount": post.content
    ? post.content.split(' ').length
    : post.readingTime * 200,
  "timeRequired": `PT${post.readingTime}M`,
  "inLanguage": "en-US",
  "isAccessibleForFree": true,
  "locationCreated": {
    "@type": "Place",
    "name": post.location || "Tokyo, Japan"
  }
});

// Function to create event page SEO config
export const origcreateEventSEO = (event) => ({
  title: `${event.title} -  International Community Building Hub| Connect & Co`,
  description: `Join ${event.title} on ${event.date}. ${event.description} Limited spots available for members.`,
  keywords: `${event.title}, networking event, ${event.location}, exclusive event`,
  url: `https://connectco.app/events/${event.slug}`,
  canonical: `https://connectco.app/events/${event.slug}`,
  openGraph: {
    title: `${event.title} -  International Community Building Hub`,
    description: `Join ${event.title} on ${event.date}. ${event.description}`,
    url: `https://connectco.app/events/${event.slug}`,
    image: event.image || `https://connectco.app/event-images/${event.slug}.jpg`,
    type: "event"
  },
  twitter: {
    card: "summary_large_image",
    title: `${event.title} -  International Community Building Hub`,
    description: `Join ${event.title} on ${event.date}. ${event.description}`,
    image: event.image || `https://connectco.app/event-images/${event.slug}.jpg`
  }
});

// EXISTING FUNCTION - REPLACE with enhanced version in SEO.jsx
export const createEventSEO = (event) => {
  if (!event) return null;
  
  // Dynamic title with enhanced AEO targeting
  const title = `${event.title} - ${event.next} | Tokyo Community Event | Connect & Co`;
  
  // Rich description combining event details for AEO
  const description = `Join ${event.title} on ${event.next} at ${event.time}. ${event.description} Limited to ${event.capacity} people. Location: ${event.location}. ${event.cost ? `Cost: ${event.cost}. ` : 'Free event. '}Priority access for members.`;
  
  // Enhanced keywords based on event type
  const baseKeywords = [
    event.title.toLowerCase(),
    ...event.tags.map(tag => tag.toLowerCase()),
    'tokyo community events',
    'english conversation tokyo',
    'expat activities tokyo',
    'international tokyo'
  ];

  // Event-type specific keywords for better discovery
  const typeSpecificKeywords = [];
  
  if (event.eventType === 'river-walks' || event.slug.includes('meguro')) {
    typeSpecificKeywords.push(
      'tokyo autumn walk', 'meguro river walk', 'nakameguro coffee',
      'autumn foliage tokyo', 'morning walk tokyo', 'scenic walk tokyo',
      'what to do in tokyo autumn', 'best autumn walks tokyo'
    );
  }
  
  if (event.eventType === 'speed-fika-connection' || event.slug.includes('speed-fika')) {
    typeSpecificKeywords.push(
      'speed fika tokyo', 'structured networking tokyo', 'deep connection events',
      'psychology questions networking', 'authentic networking tokyo', 'ginza happy hour',
      'meaningful conversations tokyo', 'connection events tokyo', 'authentic friendships'
    );
  }
  
  const keywords = [...baseKeywords, ...typeSpecificKeywords].join(', ');

  return {
    title,
    description,
    keywords,
    url: `https://connectco.app/events/${event.slug}`,
    canonical: `https://connectco.app/events/${event.slug}`,
    robots: "index, follow",
    openGraph: {
      title: event.title,
      description,
      url: `https://connectco.app/events/${event.slug}`,
      image: event.iconImage 
        ? `https://connectco.app${event.iconImage}` 
        : `https://connectco.app/events/${event.slug}-og.jpg`,
      type: "event",
      site_name: "Connect & Co",
      locale: "en_US"
    },
    twitter: {
      card: "summary_large_image",
      site: "@connectandco",
      title: event.title,
      description,
      image: event.iconImage 
        ? `https://connectco.app${event.iconImage}` 
        : `https://connectco.app/events/${event.slug}-twitter.jpg`
    },
    additionalMeta: [
      { name: "author", content: "Connect & Co" },
      { name: "publisher", content: "Connect & Co" },
      { name: "event-date", content: event.next },
      { name: "event-location", content: event.location },
      { name: "event-capacity", content: event.capacity.toString() },
      { name: "geo.region", content: "JP-13" },
      { name: "geo.placename", content: "Tokyo" },
      { property: "event:start_time", content: event.next },
      { property: "event:location:locality", content: "Tokyo" },
      { property: "event:location:country", content: "Japan" }
    ]
  };
};

// Structured data function (inject into head manually)
export const injectStructuredData = (data) => {
  // Only remove dynamically-injected scripts — never touch the static index.html schema
  const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-seo-dynamic="true"]');
  existingScripts.forEach(script => script.remove());

  // Add new structured data, tagged so it can be safely removed on next navigation
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-dynamic', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// Function to inject canonical link
export const injectCanonicalLink = (url) => {
  // Remove existing canonical link
  const existingCanonical = document.querySelector('link[rel="canonical"]');
  if (existingCanonical) {
    existingCanonical.remove();
  }

  // Add new canonical link
  const link = document.createElement('link');
  link.rel = 'canonical';
  link.href = url;
  document.head.appendChild(link);
};

// Default structured data
export const defaultStructuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Connect & Co",
  "alternateName": "Connect and Co",
  "description": "International community for exclusive networking events with priority access for members",
  "url": "https://connectco.app",
  "logo": "https://connectco.app/logo.png",
  "image": "https://connectco.app/og-image.jpg",
  "foundingDate": "2025",
  "sameAs": [
    "https://www.linkedin.com/company/connect-co",
    "https://www.instagram.com/connectandco",
    "https://twitter.com/connectandco"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "hello@connectco.app",
    "availableLanguage": ["English", "Japanese"]
  },

  "offers": {
    "@type": "Service",
    "name": "Premium Community Membership",
    "description": "Priority access to exclusive networking events worldwide",
    "provider": {
      "@type": "Organization",
      "name": "Connect & Co"
    }
  }
};

// Event-specific schema for better SEO
export const createEventSchema = (event) => ({
  "@context": "https://schema.org",
  "@type": "Event",
  "name": event.title,
  "description": event.description,
  "startDate": event.next,
  "eventStatus": "https://schema.org/EventScheduled",
  "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
  "location": {
    "@type": "Place",
    "name": event.location,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Tokyo",
      "addressCountry": "Japan"
    }
  },
  "organizer": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "offers": {
    "@type": "Offer",
    "name": "Event Registration",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  }
});

// Blog post schema


// 🆕 NEW: Conversation Starters page structured data
export const conversationStartersSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Fika Conversation Starters & Cultural Learning Hub",
  "description": "200+ thoughtful fika conversation starters across 9 categories with cultural learning concepts from around the world",
  "url": "https://connectco.app/conversation-starters",
  "mainEntity": {
    "@type": "HowTo",
    "name": "How to Start Meaningful Fika Conversations",
    "description": "Learn to master meaningful fika conversations with curated questions and cultural insights",
    "image": "https://connectco.app/conversation-starters-og.jpg",
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "200+ Conversation Questions"
      },
      {
        "@type": "HowToSupply", 
        "name": "Cultural Learning Concepts"
      },
      {
        "@type": "HowToSupply",
        "name": "Conversation Tips & Guidelines"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "Choose Your Conversation Style",
        "text": "Select from 9 categories: Ice Breakers, Cultural Exchange, Deep & Meaningful, Travel & Adventure, Creative & Hobbies, Professional Growth, New to Tokyo, Expat Life, or Home is Here"
      },
      {
        "@type": "HowToStep", 
        "name": "Learn Cultural Context",
        "text": "Explore cultural concepts like Japanese Ikigai, Swedish Lagom, Danish Hygge, and more to deepen your understanding"
      },
      {
        "@type": "HowToStep",
        "name": "Practice Active Listening",
        "text": "Use conversation do's and don'ts to create authentic connections through meaningful dialogue"
      }
    ]
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://connectco.app"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Conversation Starters",
        "item": "https://connectco.app/conversation-starters"
      }
    ]
  },
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png"
    }
  }
};

// Website navigation schema for better SEO
export const websiteNavigationSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Connect & Co",
  "url": "https://connectco.app",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://connectco.app/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  },
  "mainEntity": {
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "SiteNavigationElement",
        "position": 1,
        "name": "Events",
        "url": "https://connectco.app/events"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 2,
        "name": "Membership",
        "url": "https://connectco.app/membership"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 3,
        "name": "Conversation Starters",
        "url": "https://connectco.app/conversation-starters"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 4,
        "name": "Blog",
        "url": "https://connectco.app/blog"
      },
      {
        "@type": "SiteNavigationElement",
        "position": 5,
        "name": "Community",
        "url": "https://connectco.app/community"
      }
    ]
  }
};

// 🆕 NEW: Tokyo Expat Guide Structured Data
export const tokyoExpatGuideSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Complete Tokyo Expat Guide",
  "description": "Essential guide for expats moving to Tokyo with community building and practical survival tips",
  "url": "https://connectco.app/tokyo-expat-guide",
  "mainEntity": {
    "@type": "HowTo",
    "name": "How to Successfully Move to Tokyo as an Expat",
    "description": "Complete guide covering community building, practical essentials, and authentic connections for international professionals",
    "image": "https://connectco.app/tokyo-expat-guide-og.jpg",
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Community Access to 2,000+ International Professionals"
      },
      {
        "@type": "HowToSupply", 
        "name": "Swedish Fika Community Events"
      },
      {
        "@type": "HowToSupply",
        "name": "Practical Tokyo Survival Resources"
      },
      {
        "@type": "HowToSupply",
        "name": "Cultural Integration Support"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "Join Community First",
        "text": "Connect with 2,000+ expats who've successfully navigated Tokyo. Skip the 6-12 month isolation phase by building friendships in your first 2-3 weeks through Swedish fika traditions."
      },
      {
        "@type": "HowToStep", 
        "name": "Handle First 48 Hours",
        "text": "Essential immediate steps: airport transport, SIM card, find ATM, download key apps (Google Translate, Hyperdia, Tokyo Metro), register accommodation."
      },
      {
        "@type": "HowToStep",
        "name": "Complete First 2 Weeks Setup",
        "text": "Open bank account, get residence card, register address, set up utilities, find local doctor, learn train basics."
      },
      {
        "@type": "HowToStep",
        "name": "Build Long-term Foundation",
        "text": "Join professional networks, explore neighborhoods, learn basic Japanese, understand cultural norms, find your local community through authentic gatherings."
      }
    ]
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://connectco.app"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Tokyo Expat Guide",
        "item": "https://connectco.app/tokyo-expat-guide"
      }
    ]
  },
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png"
    }
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "International professionals and expats moving to Tokyo"
  },
  "about": [
    {
      "@type": "Thing",
      "name": "Tokyo Expat Life"
    },
    {
      "@type": "Thing", 
      "name": "International Community Building"
    },
    {
      "@type": "Thing",
      "name": "Swedish Fika Culture"
    },
    {
      "@type": "Thing",
      "name": "Tokyo Practical Living"
    }
  ]
};

// 🆕 NEW: FAQ Schema for Expat Guide (helps with featured snippets)
export const expatGuideFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How long does it take to make friends as an expat in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "With Connect & Co's community, most expats make lasting friends within 2-3 weeks instead of the typical 6-12 month isolation phase. Our Swedish fika traditions and regular events create natural opportunities for authentic connections."
      }
    },
    {
      "@type": "Question",
      "name": "What should I do in my first 48 hours in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Essential first steps: arrange airport transport, get a Japanese SIM card or pocket wifi, find nearest convenience store and ATM, download essential apps (Google Translate, Hyperdia, Tokyo Metro), and register at your accommodation."
      }
    },
    {
      "@type": "Question",
      "name": "How do I open a bank account in Tokyo as a foreigner?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You'll need your residence card, passport, and address registration. Foreigner-friendly banks include SMBC, Mizuho, and Japan Post Bank. Our community provides detailed guidance and can recommend which branches are most helpful for international residents."
      }
    },
    {
      "@type": "Question",
      "name": "What is Swedish fika and how does it help expats?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Fika is a Swedish tradition of slowing down over coffee and meaningful conversation. It's always social and focuses on authentic connection rather than networking. This creates the perfect environment for expats to build genuine friendships naturally."
      }
    },
    {
      "@type": "Question",
      "name": "How big is the Connect & Co expat community in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our community includes 2,000+ active members representing 30+ nationalities, with weekly and monthly events designed specifically for meaningful connection and cultural exchange."
      }
    }
  ]
};

// ENHANCED: Structured Data for Goal Accountability Partners
export const goalAccountabilityStructuredData = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Goal Accountability Partners",
  "alternateName": "Accountability Tribe Matching Service",
  "description": "Expert matching service connecting 6-8 like-minded people pursuing the same goal through authentic Swedish fika tradition and proven accountability systems.",
  "url": "https://connectco.app/goal-accountability-partners",
  "image": "https://connectco.app/goal-accountability-partners-og.jpg",
  "provider": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app",
    "logo": "https://connectco.app/logo.png",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "hello@connectco.app",
      "availableLanguage": ["English", "Japanese"]
    }
  },
  "serviceType": "Personal Development and Goal Achievement Support",
  "category": "Goal Achievement",
  "offers": {
    "@type": "Offer",
    "name": "6-Week Goal Accountability Program",
    "description": "Free 6-week accountability program with expert matching and community support",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "validFrom": new Date().toISOString(),
    "itemCondition": "https://schema.org/NewCondition"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Goal Categories",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Language Learning Accountability",
          "category": "Education"
        }
      },
      {
        "@type": "Offer", 
        "itemOffered": {
          "@type": "Service",
          "name": "Professional Development Accountability",
          "category": "Career"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service", 
          "name": "Fitness & Health Goal Accountability",
          "category": "Health"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Creative Project Accountability", 
          "category": "Arts"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Entrepreneurial Venture Accountability",
          "category": "Business"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Reading Challenge Accountability",
          "category": "Education"
        }
      }
    ]
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "International professionals, entrepreneurs, students, and goal-oriented individuals",
    "geographicArea": {
      "@type": "Place",
      "name": "Global with focus on Tokyo, Japan"
    }
  },
  "areaServed": [
    {
      "@type": "Place",
      "name": "Tokyo, Japan"
    },
    {
      "@type": "Place", 
      "name": "Global Online"
    }
  ],
  "serviceOutput": {
    "@type": "Thing",
    "name": "Goal Achievement and Habit Formation",
    "description": "Structured accountability system leading to goal completion"
  },
  "keywords": "goal accountability, accountability partners, habit formation, goal achievement, personal development, Swedish fika tradition, community support",
  "review": {
    "@type": "Review",
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": "4.8",
      "bestRating": "5"
    },
    "author": {
      "@type": "Organization",
      "name": "Connect & Co Community"
    }
  },
  "aggregateRating": {
    "@type": "AggregateRating", 
    "ratingValue": "4.8",
    "reviewCount": "156",
    "bestRating": "5"
  }
};

// ENHANCED: FAQ Schema for Goal Accountability Partners (helps with featured snippets)
export const goalAccountabilityFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What are Goal Accountability Partners and how do they work?",
      "acceptedAnswer": {
        "@type": "Answer", 
        "text": "Goal Accountability Partners connects you with 6-8 like-minded people pursuing the same goal through our authentic Swedish fika tradition. Groups meet weekly for check-ins, progress sharing, and mutual support. Our expert matching ensures compatible personalities, timelines, and commitment levels for maximum success."
      }
    },
    {
      "@type": "Question",
      "name": "How are accountability groups matched and formed?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our smart matching algorithm groups people by goal category, target timeline (±30 days), commitment level, meeting preferences, and accountability style. Groups are formed with 6-8 members for optimal dynamics, ensuring everyone has compatible schedules and communication preferences."
      }
    },
    {
      "@type": "Question",
      "name": "What types of goals work best for accountability partners?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Any specific, measurable goal works well: language learning (JLPT, TOEFL), fitness targets, reading challenges, professional certifications, creative projects, entrepreneurial ventures, academic studies, and skill development. The key is having a clear timeline and measurable outcome."
      }
    },
    {
      "@type": "Question",
      "name": "How long does the accountability program last?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The initial program is 6 weeks with weekly check-ins. Groups that work well together often continue independently for 3-6 months or until their goals are achieved. Many members join multiple groups for different goals throughout the year."
      }
    },
    {
      "@type": "Question", 
      "name": "What is the Swedish fika tradition in accountability?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Swedish fika emphasizes slowing down over coffee for authentic conversation and connection. In accountability groups, this means meaningful check-ins focused on genuine support rather than judgment, creating a safe space for vulnerability and growth."
      }
    },
    {
      "@type": "Question",
      "name": "Is the Goal Accountability Partners program free?",
      "acceptedAnswer": {
        "@type": "Answer", 
        "text": "Yes, the 6-week Goal Accountability Partners program is completely free. This includes expert matching, group formation, introduction emails, and ongoing support. Our mission is making goal achievement accessible through authentic community connection."
      }
    },
    {
      "@type": "Question",
      "name": "How do I know if my accountability group is a good fit?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Good accountability groups have compatible communication styles, similar commitment levels, overlapping availability, and matching goal timelines. After the first meeting, you should feel supported, understood, and motivated. If not, we can help you find a better match."
      }
    },
    {
      "@type": "Question",
      "name": "What happens if I miss check-ins or fall behind on my goal?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Life happens! Good accountability groups support each other through setbacks. The focus is on getting back on track, not judgment. Groups often adjust timelines together or provide extra support during challenging periods. Authentic accountability includes grace and flexibility."
      }
    }
  ]
};

// ENHANCED: How-To Schema for Goal Achievement Process
export const goalAchievementHowToSchema = {
  "@context": "https://schema.org", 
  "@type": "HowTo",
  "name": "How to Achieve Your Goals with Accountability Partners",
  "description": "Step-by-step guide to achieving your goals through authentic accountability partnerships and Swedish fika tradition",
  "image": "https://connectco.app/goal-achievement-guide.jpg",
  "totalTime": "P6W",
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "USD",
    "value": "0"
  },
  "supply": [
    {
      "@type": "HowToSupply",
      "name": "Specific Goal with Timeline"
    },
    {
      "@type": "HowToSupply",
      "name": "Weekly Time Commitment (1-8+ hours)"
    },
    {
      "@type": "HowToSupply", 
      "name": "Accountability Group Membership"
    },
    {
      "@type": "HowToSupply",
      "name": "Regular Check-in Schedule"
    }
  ],
  "tool": [
    {
      "@type": "HowToTool",
      "name": "Goal Accountability Partners Matching System"
    },
    {
      "@type": "HowToTool",
      "name": "Swedish Fika Communication Method"
    },
    {
      "@type": "HowToTool", 
      "name": "Progress Tracking System"
    }
  ],
  "step": [
    {
      "@type": "HowToStep",
      "name": "Apply for Accountability Partnership",
      "text": "Complete our detailed application covering your specific goal, timeline, commitment level, and communication preferences. Our matching algorithm will find 6-8 compatible partners.",
      "url": "https://connectco.app/goal-accountability-partners",
      "image": "https://connectco.app/step-1-apply.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Get Matched with Your Accountability Tribe", 
      "text": "Within 5-7 days, receive email introductions to your matched group members. Everyone shares the same goal category, similar timeline, and compatible communication style.",
      "image": "https://connectco.app/step-2-matching.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Schedule Your First Group Check-in",
      "text": "Coordinate with your group to schedule the first meeting. Use Swedish fika principles: focus on authentic connection, share progress honestly, and support each other's journey.",
      "image": "https://connectco.app/step-3-first-meeting.jpg"
    },
    {
      "@type": "HowToStep", 
      "name": "Establish Weekly Accountability Rhythm",
      "text": "Create a consistent weekly check-in schedule. Share progress, challenges, wins, and next week's commitments. Use accountability styles that match your group's preferences.",
      "image": "https://connectco.app/step-4-weekly-rhythm.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Support Each Other Through Challenges",
      "text": "When motivation dips or obstacles arise, lean on your accountability tribe. Authentic support includes both celebration of wins and gentle accountability for getting back on track.",
      "image": "https://connectco.app/step-5-support.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Achieve Your Goal Together",
      "text": "Complete your 6-week program and celebrate goal achievement with your accountability tribe. Many groups continue independently or join new groups for their next goals.",
      "image": "https://connectco.app/step-6-achieve.jpg"
    }
  ]
};

// ENHANCED: Organization Schema for Connect & Co Goal Services
export const connectCoGoalServicesSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Connect & Co Goal Accountability Partners",
  "alternateName": "Connect & Co Accountability Services",
  "url": "https://connectco.app/goal-accountability-partners", 
  "logo": "https://connectco.app/goal-accountability-logo.png",
  "description": "Expert goal accountability matching service connecting like-minded individuals through authentic Swedish fika tradition and proven accountability systems",
  "founder": {
    "@type": "Person",
    "name": "Connect & Co Team"
  },
  "foundingDate": "2024",
  "knowsAbout": [
    "Goal Achievement",
    "Accountability Partnerships", 
    "Habit Formation",
    "Swedish Fika Tradition",
    "Community Building",
    "Personal Development",
    "Group Dynamics",
    "Peer Support Systems"
  ],
  "serviceArea": {
    "@type": "Place",
    "name": "Global with focus on Tokyo, Japan"
  },
  "hasOfferCatalog": {
    "@type": "OfferCatalog", 
    "name": "Goal Achievement Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Goal Accountability Partner Matching",
          "description": "Expert matching with 6-8 compatible accountability partners"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service", 
          "name": "Swedish Fika Accountability Groups",
          "description": "Authentic accountability through Swedish conversation tradition"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "6-Week Structured Goal Achievement Program", 
          "description": "Guided program with weekly check-ins and expert support"
        }
      }
    ]
  },
  "makesOffer": {
    "@type": "Offer",
    "itemOffered": {
      "@type": "Service", 
      "name": "Free Goal Accountability Partners Program",
      "description": "Completely free 6-week accountability program with expert matching and ongoing support"
    },
    "price": "0",
    "priceCurrency": "USD"
  }
};

export const goalAccountabilityFAQStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Goal Accountability Partners FAQ",
  "description": "Frequently asked questions about Goal Accountability Partners program",
  "url": "https://connectco.app/goal-accountability-faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What exactly are Goal Accountability Partners?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Goal Accountability Partners is a free program that connects you with 6-8 like-minded people pursuing the same goal through our authentic Swedish fika tradition. Instead of surface-level networking, we focus on genuine support, weekly check-ins, and shared success."
      }
    },
    {
      "@type": "Question",
      "name": "How does the matching process work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our smart algorithm matches you based on goal category, target timeline (±30 days), commitment level, accountability style, meeting preferences, and availability. We consider factors like whether you prefer supportive encouragement or direct challenges, your timezone, and preferred communication methods."
      }
    },
    {
      "@type": "Question",
      "name": "Is the program really free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, completely free! Goal Accountability Partners is our way of giving back to the community and helping people achieve meaningful goals through authentic connection. There are no hidden fees, upsells, or premium tiers."
      }
    },
    {
      "@type": "Question",
      "name": "What kind of success rates do you see?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "About 78% of participants achieve their goal or make significant progress beyond what they would have alone. Even more importantly, 91% report feeling more motivated and supported in their goal pursuit."
      }
    }
  ]
};


// How-To Schema for Tokyo Survival
export const tokyoSurvivalHowToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Successfully Move to Tokyo as an Expat",
  "description": "Complete step-by-step guide for international professionals moving to Tokyo, covering all essential setup and integration steps",
  "image": "https://connectco.app/tokyo-survival-how-to.jpg",
  "totalTime": "P30D",
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "JPY",
    "value": "100000-500000"
  },
  "supply": [
    {
      "@type": "HowToSupply",
      "name": "Valid Passport with Visa"
    },
    {
      "@type": "HowToSupply",
      "name": "Initial Funding (¥300,000-500,000)"
    },
    {
      "@type": "HowToSupply",
      "name": "Accommodation Arrangement"
    },
    {
      "@type": "HowToSupply",
      "name": "Emergency Contact Information"
    }
  ],
  "tool": [
    {
      "@type": "HowToTool",
      "name": "Google Translate App"
    },
    {
      "@type": "HowToTool",
      "name": "Hyperdia Train App"
    },
    {
      "@type": "HowToTool",
      "name": "Tokyo Metro App"
    }
  ],
  "step": [
    {
      "@type": "HowToStep",
      "name": "Complete First 48 Hours Essentials",
      "text": "Navigate from airport to accommodation, get cash from 7-Eleven ATM, buy IC card for trains, download essential apps, find nearest convenience store, and get SIM card or pocket WiFi.",
      "url": "https://connectco.app/tokyo-survival-guide#first-48-hours",
      "image": "https://connectco.app/step-1-arrival.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Set Up Banking and Finances",
      "text": "Open bank account at foreigner-friendly bank (Seven Bank, Japan Post, or MUFG), set up international money transfer method, and understand ATM locations and cash culture.",
      "url": "https://connectco.app/tokyo-survival-guide#banking",
      "image": "https://connectco.app/step-2-banking.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Master Transportation System",
      "text": "Learn train lines (JR Yamanote, Tokyo Metro), get comfortable with IC card usage, understand rush hour patterns, and register bicycle if needed.",
      "url": "https://connectco.app/tokyo-survival-guide#transportation",
      "image": "https://connectco.app/step-3-transport.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Secure Long-term Housing",
      "text": "Research neighborhoods, understand rental process (reikin, shikikin, deposits), work with foreigner-friendly agencies, and set up utilities.",
      "url": "https://connectco.app/tokyo-survival-guide#housing",
      "image": "https://connectco.app/step-4-housing.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Complete Legal and Healthcare Setup",
      "text": "Enroll in National Health Insurance, register residence at ward office, get My Number card, and find English-speaking medical facilities.",
      "url": "https://connectco.app/tokyo-survival-guide#healthcare",
      "image": "https://connectco.app/step-5-legal.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Integrate Culturally and Socially",
      "text": "Learn essential Japanese phrases, understand business etiquette, respect social customs, and build community connections through authentic gatherings.",
      "url": "https://connectco.app/tokyo-survival-guide#culture",
      "image": "https://connectco.app/step-6-culture.jpg"
    }
  ]
};

// FAQ Schema for Tokyo Survival Guide
export const tokyoSurvivalFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Tokyo Survival Guide FAQ",
  "description": "Frequently asked questions about moving to and living in Tokyo as an expat",
  "url": "https://connectco.app/tokyo-survival-guide#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How much money do I need to move to Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Budget ¥300,000-500,000 for initial setup costs including apartment deposits (4-6 months rent equivalent), first month expenses, and emergency fund. Monthly living costs range from ¥150,000-300,000 depending on lifestyle and housing choices."
      }
    },
    {
      "@type": "Question",
      "name": "Which bank is best for foreigners in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Seven Bank is easiest for foreigners with English ATMs nationwide and 1-2 day account opening. Japan Post Bank has the largest ATM network. MUFG Bank offers better international services but requires higher minimum balance and appointments."
      }
    },
    {
      "@type": "Question",
      "name": "How do I navigate Tokyo's train system?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Get a Suica or Pasmo IC card at any station (¥500 deposit + ¥1,000 minimum). Download Google Maps, Hyperdia, and Tokyo Metro apps. Learn the JR Yamanote circular line first. Avoid rush hours (7:30-9:30 AM, 5:30-7:30 PM) when possible."
      }
    },
    {
      "@type": "Question",
      "name": "What should I do in my first 48 hours in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Get cash from 7-Eleven ATM, buy IC card for trains, download Google Translate with camera function, find nearest convenience store, get SIM card or pocket WiFi, locate your train station, and register at your accommodation."
      }
    },
    {
      "@type": "Question",
      "name": "How do I find expat-friendly housing in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Use foreigner-friendly agencies like Leopalace21, Sakura House, or Real Estate Japan. Popular expat areas include Shibuya, Shinjuku, Roppongi (expensive) or Kichijoji, Nakameguro (better value). Budget 4-6 months rent for upfront costs including key money and deposits."
      }
    },
    {
      "@type": "Question",
      "name": "Is the Tokyo Survival Guide really free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, completely free! The Tokyo Survival Guide is our way of supporting the international community in Tokyo. No hidden fees, no upsells - just practical, tested advice from expats who've successfully made Tokyo their home."
      }
    },
    {
      "@type": "Question",
      "name": "How long does it take to get settled in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Basic setup (banking, phone, transport) takes 1-2 weeks. Finding permanent housing can take 2-4 weeks. Full cultural adaptation and feeling 'at home' typically takes 3-6 months, but varies by individual and community connections."
      }
    },
    {
      "@type": "Question",
      "name": "Do I need to speak Japanese to live in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "English works for basic needs in central Tokyo, but learning Japanese greatly improves your experience. Start with essential phrases for daily interactions. Many services have English support, but housing and banking may require Japanese assistance."
      }
    }
  ]
};

export const tokyoSurvivalBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://connectco.app"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Resources",
      "item": "https://connectco.app/resources"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Tokyo Survival Guide",
      "item": "https://connectco.app/tokyo-survival-guide"
    }
  ]
};

export const guideDownloadEventSchema = {
  "@context": "https://schema.org",
  "@type": "Event",
  "name": "Tokyo Survival Guide 2026 Launch",
  "description": "Launch of comprehensive Tokyo survival guide for international expats and professionals",
  "startDate": "2024-08-08",
  "endDate": "2024-12-31",
  "eventStatus": "https://schema.org/EventScheduled",
  "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
  "location": {
    "@type": "VirtualLocation",
    "url": "https://connectco.app/tokyo-survival-guide"
  },
  "organizer": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "validFrom": "2024-08-08",
    "name": "Free Guide Download"
  },
  "audience": {
    "@type": "Audience",
    "audienceType": "International expats moving to Tokyo"
  }
};

export const tokyoSurvivalGuideSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Tokyo Survival Guide 2026 - Complete Expat Handbook",
  "description": "Free comprehensive Tokyo survival guide for international expats covering banking, housing, transportation, healthcare, and cultural integration",
  "url": "https://connectco.app/tokyo-survival-guide",
  "mainEntity": {
    "@type": "DigitalDocument",
    "name": "Tokyo Survival Guide 2026 - Complete Expat Handbook",
    "description": "47-page comprehensive guide covering all essential aspects of moving to and thriving in Tokyo as an international expat",
    "author": {
      "@type": "Organization",
      "name": "Connect & Co",
      "url": "https://connectco.app"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Connect & Co",
      "logo": {
        "@type": "ImageObject",
        "url": "https://connectco.app/logo.png"
      }
    },
    "datePublished": "2024-08-08",
    "dateModified": "2024-08-08",
    "inLanguage": "en-US",
    "isAccessibleForFree": true,
    "license": "https://creativecommons.org/licenses/by-nc-sa/4.0/",
    "genre": [
      "Survival Guide",
      "Expat Handbook", 
      "Travel Guide",
      "Living Abroad Guide"
    ],
    "about": [
      {
        "@type": "Thing",
        "name": "Tokyo Banking for Expats"
      },
      {
        "@type": "Thing", 
        "name": "Tokyo Transportation Guide"
      },
      {
        "@type": "Thing",
        "name": "Tokyo Housing for Foreigners"
      },
      {
        "@type": "Thing",
        "name": "Japanese Healthcare System"
      },
      {
        "@type": "Thing",
        "name": "Cultural Integration Japan"
      },
      {
        "@type": "Thing",
        "name": "Expat Legal Requirements Japan"
      }
    ],
    "teaches": [
      "How to open a Japanese bank account as a foreigner",
      "Navigating Tokyo's transportation system",
      "Finding expat-friendly housing in Tokyo", 
      "Understanding Japanese healthcare and insurance",
      "Essential cultural integration tips",
      "Legal requirements for living in Japan"
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": "International expats and professionals moving to Tokyo"
    },
    "downloadUrl": "https://connectco.app/tokyo-survival-guide",
    "fileFormat": "text/plain",
    "contentSize": "47 pages",
    "keywords": "tokyo survival guide, expat handbook, tokyo banking, tokyo housing, tokyo transportation, japan healthcare, cultural integration"
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://connectco.app"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Tokyo Expat Guide",
        "item": "https://connectco.app/tokyo-expat-guide"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Tokyo Survival Guide",
        "item": "https://connectco.app/tokyo-survival-guide"
      }
    ]
  },
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD", 
    "availability": "https://schema.org/InStock",
    "itemOffered": {
      "@type": "DigitalDocument",
      "name": "Tokyo Survival Guide 2026"
    }
  }
};

// Add these configurations to your SEO.jsx file

// 1. COMPREHENSIVE SEO CONFIG FOR CALCULATOR
export const tokyoExpatCalculatorSEO = {
  title: "Free Tokyo Expat Integration Calculator - Cost, Time & Loneliness Analysis | Connect & Co",
  description: "Calculate how much time, money and loneliness you can save choosing authentic fika community over traditional networking. Free tool used by 2,800+ Tokyo expats to optimize their integration strategy.",
  keywords: "tokyo expat calculator, expat integration cost, tokyo networking analysis, expat friendship timeline, tokyo social budget calculator, japan expat tool, tokyo community cost analysis, expat loneliness calculator, tokyo integration strategy, international professionals tokyo, expat social investment, tokyo community comparison, networking cost calculator, expat friend-making timeline, tokyo social efficiency calculator",
  url: "https://connectco.app/tokyo-expat-calculator",
  canonical: "https://connectco.app/tokyo-expat-calculator",
  robots: "index, follow",
  openGraph: {
    title: "Free Tokyo Expat Integration Calculator - Cost, Time & Loneliness Analysis",
    description: "Calculate how much time, money and loneliness you can save choosing authentic community over traditional networking. Used by 2,800+ Tokyo expats.",
    url: "https://connectco.app/tokyo-expat-calculator",
    image: "https://connectco.app/tokyo-expat-calculator-og.jpg",
    imageAlt: "Tokyo Expat Calculator - Compare integration strategies with cost and time analysis",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Free Tokyo Expat Integration Calculator - Cost, Time & Loneliness Analysis",
    description: "Calculate how much time, money and loneliness you can save choosing authentic community over traditional networking. Free tool for Tokyo expats.",
    image: "https://connectco.app/tokyo-expat-calculator-twitter.jpg",
    imageAlt: "Tokyo Expat Calculator showing cost savings and integration timeline analysis"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "application-name", content: "Tokyo Expat Integration Calculator" },
    { name: "apple-mobile-web-app-title", content: "Tokyo Expat Calculator" },
    { name: "theme-color", content: "#8b5cf6" },
    { name: "format-detection", content: "telephone=no" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/jpeg" },
    { name: "tool-type", content: "calculator" },
    { name: "tool-category", content: "expat integration" },
    { name: "target-audience", content: "International expats, digital nomads, professionals in Tokyo" },
    { name: "geographic-coverage", content: "Tokyo, Japan" }
  ]
};

// 2. STRUCTURED DATA FOR CALCULATOR TOOL
export const calculatorStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Tokyo Expat Integration Calculator",
  "alternateName": "Tokyo Expat Cost Calculator",
  "description": "Free calculator tool that helps international expats in Tokyo compare integration strategies, analyze costs, and optimize their social investment for faster community building",
  "url": "https://connectco.app/tokyo-expat-calculator",
  "applicationCategory": "UtilityApplication",
  "operatingSystem": "Web Browser",
  "browserRequirements": "HTML5, JavaScript enabled",
  "softwareVersion": "1.0",
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png",
      "width": "200",
      "height": "60"
    }
  },
  "datePublished": "2024-08-13",
  "dateModified": "2024-08-13",
  "inLanguage": "en-US",
  "isAccessibleForFree": true,
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  },
  "featureList": [
    "Cost comparison between traditional networking and authentic community",
    "Time-to-friendship analysis based on cultural background",
    "Loneliness prevention calculator",
    "Personalized integration strategy recommendations",
    "Budget-responsive community cost analysis",
    "Cultural background optimization",
    "Japanese language level integration factors"
  ],
  "audience": {
    "@type": "Audience",
    "audienceType": "International expats and professionals in Tokyo",
    "geographicArea": {
      "@type": "Place",
      "name": "Tokyo, Japan"
    }
  },
  "keywords": "tokyo expat, integration calculator, networking costs, community building, expat tools, social investment, friendship timeline",
  "about": [
    {
      "@type": "Thing",
      "name": "Expat Integration"
    },
    {
      "@type": "Thing", 
      "name": "Tokyo Community Building"
    },
    {
      "@type": "Thing",
      "name": "Social Investment Analysis"
    },
    {
      "@type": "Thing",
      "name": "Networking Cost Optimization"
    }
  ]
};

// 3. FAQ STRUCTURED DATA FOR CALCULATOR
export const calculatorFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Tokyo Expat Calculator FAQ",
  "description": "Frequently asked questions about the Tokyo Expat Integration Calculator",
  "url": "https://connectco.app/tokyo-expat-calculator#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How accurate is the Tokyo Expat Integration Calculator?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The calculator is based on data from 2,800+ expats in Tokyo and factors in cultural background, Japanese language level, budget, and personality type. Results are estimates based on community patterns, with 87% of users finding the predictions align with their actual experience within 20% variance."
      }
    },
    {
      "@type": "Question", 
      "name": "Is the Tokyo Expat Calculator really free?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, completely free! No email required to use the calculator. Optional email capture provides a detailed integration report but the core calculator and results are always free to use and share."
      }
    },
    {
      "@type": "Question",
      "name": "What factors does the calculator consider?",
      "acceptedAnswer": {
        "@type": "Answer", 
        "text": "The calculator analyzes your time in Tokyo, current friend count, weekly social budget, cultural background, Japanese language level, work status, personality type, and social activity level to provide personalized integration strategy recommendations."
      }
    },
    {
      "@type": "Question",
      "name": "How does cultural background affect the calculations?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Different cultures have varying integration speeds in Tokyo. Scandinavians often adapt faster due to cultural alignment with fika traditions, while Germans may take longer due to structured social expectations. The calculator adjusts timelines and strategies accordingly."
      }
    },
    {
      "@type": "Question",
      "name": "Can I trust the cost savings estimates?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Cost estimates are based on actual member spending patterns and Tokyo market rates. Traditional networking events average ¥150,000-400,000 annually, while authentic community approaches typically cost 20-40% of your social budget, leading to significant savings."
      }
    },
    {
      "@type": "Question",
      "name": "What makes fika community different from regular networking?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Swedish fika tradition emphasizes authentic connection over transactional networking. Small groups (20-30 people), consistent venues, and focus on genuine conversation lead to deeper friendships faster than large, rotating networking events."
      }
    },
    {
      "@type": "Question",
      "name": "How often should I recalculate my integration strategy?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Recalculate when major factors change: improved Japanese level, budget changes, job status updates, or after 3-6 months to track progress. Your integration strategy should evolve as you become more established in Tokyo."
      }
    }
  ]
};

// 4. HOW-TO SCHEMA FOR USING THE CALCULATOR
export const calculatorHowToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Use the Tokyo Expat Integration Calculator",
  "description": "Step-by-step guide to calculating your optimal Tokyo integration strategy using cultural background, budget, and personal factors",
  "image": "https://connectco.app/calculator-how-to-guide.jpg",
  "totalTime": "PT5M",
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "USD", 
    "value": "0"
  },
  "supply": [
    {
      "@type": "HowToSupply",
      "name": "Current Tokyo experience (months)"
    },
    {
      "@type": "HowToSupply",
      "name": "Weekly social/networking budget"
    },
    {
      "@type": "HowToSupply",
      "name": "Cultural background information"
    }
  ],
  "tool": [
    {
      "@type": "HowToTool",
      "name": "Tokyo Expat Integration Calculator",
      "url": "https://connectco.app/tokyo-expat-calculator"
    }
  ],
  "step": [
    {
      "@type": "HowToStep",
      "name": "Enter Your Tokyo Experience",
      "text": "Input how long you've been in Tokyo (0-24+ months) and your current number of close friends. This establishes your integration baseline.",
      "url": "https://connectco.app/tokyo-expat-calculator#experience",
      "image": "https://connectco.app/step-1-experience.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Set Your Social Budget",
      "text": "Enter your weekly social/networking budget (¥2,000-¥20,000+) and typical number of social events per week. This determines cost-effectiveness calculations.",
      "image": "https://connectco.app/step-2-budget.jpg"
    },
    {
      "@type": "HowToStep", 
      "name": "Choose Cultural Background",
      "text": "Select your cultural background from American, British, German, French, Australian, Canadian, Scandinavian, or Other. Each has different integration patterns in Tokyo.",
      "image": "https://connectco.app/step-3-culture.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Set Personal Factors",
      "text": "Choose your Japanese language level, work status, and social personality type. These factors significantly affect integration speed and community fit.",
      "image": "https://connectco.app/step-4-personal.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Calculate Integration Strategy", 
      "text": "Click the calculate button to generate your personalized comparison between traditional networking and authentic community approaches with cost, time, and friendship analysis.",
      "image": "https://connectco.app/step-5-calculate.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Review Results and Recommendations",
      "text": "Analyze your cost savings, time-to-friendship estimates, and cultural insights. Use the personalized recommendations to optimize your Tokyo integration strategy.",
      "image": "https://connectco.app/step-6-results.jpg"
    }
  ]
};

// 5. ENHANCED BREADCRUMB SCHEMA
export const calculatorBreadcrumbSchema = {
  "@context": "https://schema.org", 
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://connectco.app"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Tokyo Expat Guide",
      "item": "https://connectco.app/tokyo-expat-guide"
    },
    {
      "@type": "ListItem",
      "position": 3, 
      "name": "Expat Calculator",
      "item": "https://connectco.app/tokyo-expat-calculator"
    }
  ]
};

// 6. PRODUCT SCHEMA (treating calculator as a SaaS product)
export const calculatorProductSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Tokyo Expat Integration Calculator",
  "description": "Free online calculator for international expats to optimize their Tokyo integration strategy",
  "url": "https://connectco.app/tokyo-expat-calculator",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "247",
    "bestRating": "5"
  },
  "author": {
    "@type": "Organization",
    "name": "Connect & Co"
  }
};

// Community Guidelines SEO
// Community Guidelines SEO Configuration - Add to src/components/SEO.jsx

// 🆕 NEW: Community Guidelines SEO Configuration
export const communityGuidelinesSEO = {
  title: "Community Guidelines - Swedish Fika Principles & Authentic Connection Standards | Connect & Co",
  description: "Our community guidelines built on Swedish Fika tradition: friendship-first approach, authentic connection, inclusive welcome, and transparent communication. Learn our standards for maintaining a safe, supportive international community in Tokyo and worldwide.",
  keywords: "community guidelines, Swedish fika tradition, authentic networking standards, international community rules, friendship-first community, inclusive networking, authentic connection principles, community safety standards, networking etiquette, international professional community guidelines, tokyo expat community standards, authentic relationship building, meaningful connection standards, community behavior guidelines",
  url: "https://connectco.app/community-guidelines",
  canonical: "https://connectco.app/community-guidelines",
  robots: "index, follow",
  openGraph: {
    title: "Community Guidelines - Swedish Fika Principles & Authentic Connection Standards",
    description: "Our community guidelines built on Swedish Fika tradition: friendship-first approach, authentic connection, inclusive welcome for international professionals worldwide.",
    url: "https://connectco.app/community-guidelines",
    image: "https://connectco.app/community-guidelines-og.jpg",
    imageAlt: "Connect & Co Community Guidelines - Swedish Fika tradition meets authentic networking standards",
    type: "website",
    site_name: "Connect & Co",
    locale: "en_US"
  },
  twitter: {
    card: "summary_large_image",
    site: "@connectandco",
    title: "Community Guidelines - Swedish Fika Principles & Authentic Connection Standards",
    description: "Community standards built on Swedish Fika tradition: friendship-first approach, authentic connection, inclusive welcome for international professionals.",
    image: "https://connectco.app/community-guidelines-twitter.jpg",
    imageAlt: "Connect & Co Community Guidelines featuring Swedish Fika principles and authentic networking standards"
  },
  additionalMeta: [
    { name: "author", content: "Connect & Co" },
    { name: "publisher", content: "Connect & Co" },
    { name: "application-name", content: "Connect & Co Community Guidelines" },
    { name: "apple-mobile-web-app-title", content: "Community Guidelines" },
    { name: "theme-color", content: "#f59e0b" },
    { name: "format-detection", content: "telephone=no" },
    { property: "article:author", content: "Connect & Co" },
    { property: "article:publisher", content: "https://connectco.app" },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:image:type", content: "image/jpeg" },
    { name: "document-type", content: "Community Guidelines" },
    { name: "document-classification", content: "Policy" },
    { name: "coverage", content: "Global International Community" },
    { name: "target-audience", content: "International professionals, expats, community builders" },
    { name: "content-category", content: "Community Standards" },
    { name: "policy-type", content: "Community Guidelines" },
    { name: "community-focus", content: "Swedish Fika Tradition, Authentic Networking" },
    { name: "geographic-scope", content: "Tokyo, Japan and International" }
  ]
};

// 🆕 NEW: Community Guidelines Structured Data
export const communityGuidelinesStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Community Guidelines - Swedish Fika Principles & Authentic Connection Standards",
  "description": "Comprehensive community guidelines for international networking community built on Swedish Fika tradition",
  "url": "https://connectco.app/community-guidelines",
  "datePublished": "2024-08-19",
  "dateModified": "2024-08-19",
  "inLanguage": "en-US",
  "isPartOf": {
    "@type": "WebSite",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "mainEntity": {
    "@type": "CreativeWork",
    "name": "Connect & Co Community Guidelines",
    "description": "Community standards document outlining Swedish Fika principles, authentic connection values, and safety protocols for international networking community",
    "author": {
      "@type": "Organization",
      "name": "Connect & Co",
      "url": "https://connectco.app"
    },
    "publisher": {
      "@type": "Organization", 
      "name": "Connect & Co",
      "logo": {
        "@type": "ImageObject",
        "url": "https://connectco.app/logo.png",
        "width": "200",
        "height": "60"
      }
    },
    "about": [
      {
        "@type": "Thing",
        "name": "Swedish Fika Tradition"
      },
      {
        "@type": "Thing",
        "name": "Authentic Networking"
      },
      {
        "@type": "Thing",
        "name": "Community Safety Standards"
      },
      {
        "@type": "Thing",
        "name": "International Professional Community"
      },
      {
        "@type": "Thing",
        "name": "Friendship-First Approach"
      },
      {
        "@type": "Thing", 
        "name": "Inclusive Community Building"
      }
    ],
    "teaches": [
      "Swedish Fika principles for authentic connection",
      "Community values: friendship-first, authenticity, inclusion", 
      "Behavioral standards and expectations",
      "Safety reporting procedures and protocols",
      "Maintaining healthy community dynamics"
    ],
    "audience": {
      "@type": "Audience",
      "audienceType": "International professionals, expats, and community builders",
      "geographicArea": {
        "@type": "Place",
        "name": "Global with focus on Tokyo, Japan"
      }
    },
    "keywords": "community guidelines, Swedish fika, authentic networking, international community, friendship building, community safety",
    "genre": [
      "Community Guidelines",
      "Policy Document",
      "Community Standards",
      "Behavioral Guidelines"
    ]
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://connectco.app"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Community Guidelines",
        "item": "https://connectco.app/community-guidelines"
      }
    ]
  },
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png"
    }
  },
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "itemOffered": {
      "@type": "Service",
      "name": "Community Membership",
      "description": "Access to international networking community with Swedish Fika tradition"
    }
  }
};

// 🆕 NEW: Community Guidelines FAQ Structured Data
export const communityGuidelinesFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Community Guidelines FAQ",
  "description": "Frequently asked questions about Connect & Co community guidelines and Swedish Fika principles",
  "url": "https://connectco.app/community-guidelines#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Swedish Fika and how does it shape your community?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Swedish Fika is the art of slowing down, connecting authentically, and sharing genuine moments over coffee. In our community, Fika principles mean creating space for real human connection without agenda, focusing on authentic presence and meaningful conversation rather than transactional networking."
      }
    },
    {
      "@type": "Question",
      "name": "What does 'friendship-first' approach mean?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our friendship-first approach means events are designed to help people form genuine connections and support networks. While romantic relationships and business connections may naturally develop over time through authentic friendship, they should never be the primary motivation for joining our community."
      }
    },
    {
      "@type": "Question",
      "name": "What behaviors are not acceptable in your community?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We don't tolerate using community for hidden agendas (dating, sales pitches), making anyone feel uncomfortable or unsafe, persistent pursuit after being asked to stop, discriminatory behavior, or disrupting group dynamics for personal conversations. Serious violations result in immediate removal."
      }
    },
    {
      "@type": "Question",
      "name": "How do you ensure community safety and handle reports?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We have a comprehensive response process: immediate safety assessment, investigation of incidents, clear communication with violators about expectations, appropriate consequences (education to permanent removal), and follow-up to ensure affected members feel safe and supported. All reports are handled confidentially."
      }
    },
    {
      "@type": "Question",
      "name": "Who can join your community and what do you expect from members?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our community welcomes international professionals, expats, and locals interested in authentic connection. We expect members to show genuine interest in making friends across all demographics, engage equally with all community members, focus on getting to know people as whole human beings, and embrace Swedish Fika values of authentic presence."
      }
    },
    {
      "@type": "Question",
      "name": "How do I report inappropriate behavior or violations?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Contact Mark directly at mark@connectco.app with subject 'Community Safety Concern' or speak with him at any event. For immediate safety, remove yourself from the situation and find Mark or another trusted community member. Document details about what happened, when, and any witnesses."
      }
    },
    {
      "@type": "Question",
      "name": "What happens if I violate community guidelines?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Our response depends on the severity and nature of the violation. We start with education and clear communication about expectations. Repeated violations may result in temporary suspension, while serious violations (harassment, making anyone unsafe, persistent boundary violations) result in immediate permanent removal."
      }
    },
    {
      "@type": "Question",
      "name": "Can I appeal if I feel I've been unfairly treated?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, you can request a review by emailing mark@connectco.app with 'Appeals Review' in the subject line. Include your perspective on the situation and any relevant context. All appeals are reviewed fairly and confidentially."
      }
    }
  ]
};

// 🆕 NEW: Community Guidelines Policy Structured Data
export const communityGuidelinesPolicySchema = {
  "@context": "https://schema.org",
  "@type": "DigitalDocument",
  "name": "Connect & Co Community Guidelines",
  "description": "Official community guidelines document outlining Swedish Fika principles, behavioral standards, and safety protocols for international networking community",
  "url": "https://connectco.app/community-guidelines",
  "datePublished": "2024-08-19",
  "dateModified": "2024-08-19",
  "version": "1.0",
  "inLanguage": "en-US",
  "isAccessibleForFree": true,
  "license": "https://connectco.app/terms",
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app",
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "mark@connectco.app",
      "availableLanguage": ["English", "Japanese"]
    }
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png",
      "width": "200", 
      "height": "60"
    }
  },
  "about": [
    {
      "@type": "Thing",
      "name": "Community Standards",
      "description": "Standards for behavior and interaction in international networking community"
    },
    {
      "@type": "Thing",
      "name": "Swedish Fika Culture",
      "description": "Traditional Swedish approach to authentic social connection over coffee"
    },
    {
      "@type": "Thing",
      "name": "Safety Protocols",
      "description": "Procedures for maintaining safe and inclusive community environment"
    }
  ],
  "teaches": [
    "Swedish Fika principles for meaningful connection",
    "Community values and behavioral expectations",
    "Safety reporting and response procedures",
    "Maintaining inclusive community environment"
  ],
  "audience": {
    "@type": "Audience",
    "audienceType": "Community members, international professionals, expats"
  },
  "genre": [
    "Policy Document",
    "Community Guidelines", 
    "Behavioral Standards"
  ],
  "keywords": "community guidelines, Swedish fika tradition, networking standards, community safety, international professionals",
  "hasPart": [
    {
      "@type": "Article",
      "name": "Swedish Fika Foundation",
      "description": "Core principles of Swedish Fika tradition that guide community interaction"
    },
    {
      "@type": "Article", 
      "name": "Community Values",
      "description": "Four core values: friendship-first, authentic fika spirit, inclusive welcome, transparent communication"
    },
    {
      "@type": "Article",
      "name": "Community Standards", 
      "description": "Specific behaviors that are celebrated versus those that cross community boundaries"
    },
    {
      "@type": "Article",
      "name": "Safety and Reporting",
      "description": "Procedures for reporting violations and community response protocols"
    }
  ]
};

// 🆕 NEW: Community Guidelines How-To Schema
export const communityGuidelinesHowToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Be a Great Community Member",
  "description": "Step-by-step guide to participating authentically in Swedish Fika-based international networking community",
  "image": "https://connectco.app/community-guidelines-how-to.jpg",
  "totalTime": "PT0S", // Ongoing
  "supply": [
    {
      "@type": "HowToSupply",
      "name": "Genuine interest in making friends across all demographics"
    },
    {
      "@type": "HowToSupply",
      "name": "Commitment to Swedish Fika values of authentic presence"
    },
    {
      "@type": "HowToSupply",
      "name": "Respect for community boundaries and guidelines"
    }
  ],
  "step": [
    {
      "@type": "HowToStep",
      "name": "Embrace Friendship-First Approach",
      "text": "Come with genuine interest in making friends across all demographics. Engage equally with all community members, not just those you find attractive or professionally useful. Focus on getting to know people as whole human beings.",
      "image": "https://connectco.app/step-friendship-first.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Practice Authentic Fika Spirit",
      "text": "Show up as yourself, engage with the whole group, and leave hidden agendas at home. Be genuinely curious about others' stories, share authentically about your own life, and maintain a relaxed, pressure-free atmosphere.",
      "image": "https://connectco.app/step-authentic-fika.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Foster Inclusive Welcome",
      "text": "Help every person feel safe and comfortable being themselves. Welcome newcomers warmly, engage in group conversations rather than exclusive side discussions, and respect different comfort levels with sharing.",
      "image": "https://connectco.app/step-inclusive-welcome.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Maintain Transparent Communication",
      "text": "Be honest about your intentions and respect when someone says no to any invitation. If romantic interest develops through genuine friendship, be transparent about changing intentions and take those conversations outside community events.",
      "image": "https://connectco.app/step-transparent-communication.jpg"
    },
    {
      "@type": "HowToStep",
      "name": "Support Community Safety",
      "text": "Trust your instincts about concerning behavior, speak up when needed, protect the community space together, and report any violations promptly to maintain a safe environment for all members.",
      "image": "https://connectco.app/step-community-safety.jpg"
    }
  ]
};

// 🆕 NEW: Organization Schema for Community Guidelines
export const communityGuidelinesOrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Connect & Co",
  "alternateName": "Connect and Co Community",
  "url": "https://connectco.app",
  "logo": "https://connectco.app/logo.png",
  "description": "International networking community built on Swedish Fika tradition for authentic connection and friendship building",
  "foundingDate": "2024",
  "address": {
    "@type": "PostalAddress", 
    "addressLocality": "Tokyo",
    "addressCountry": "Japan"
  },
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "community guidelines",
    "email": "mark@connectco.app",
    "availableLanguage": ["English", "Japanese"]
  },
  "knowsAbout": [
    "Swedish Fika Tradition",
    "International Networking",
    "Community Building",
    "Authentic Connection",
    "Cross-Cultural Communication",
    "Professional Networking Ethics",
    "Inclusive Community Practices"
  ],
  "hasPolicy": [
    {
      "@type": "DigitalDocument",
      "name": "Community Guidelines",
      "url": "https://connectco.app/community-guidelines",
      "description": "Comprehensive guidelines for community participation based on Swedish Fika principles"
    },
    {
      "@type": "DigitalDocument", 
      "name": "Privacy Policy",
      "url": "https://connectco.app/privacy"
    },
    {
      "@type": "DigitalDocument",
      "name": "Terms of Service", 
      "url": "https://connectco.app/terms"
    }
  ],
  "memberOf": {
    "@type": "Organization",
    "name": "International Professional Networking Community"
  },
  "seeks": {
    "@type": "Demand",
    "name": "Community Members",
    "description": "International professionals and expats interested in authentic connection through Swedish Fika tradition"
  }
};

// 🆕 NEW: Breadcrumb Schema for Community Guidelines
export const communityGuidelinesBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://connectco.app"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Community",
      "item": "https://connectco.app#community"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Community Guidelines",
      "item": "https://connectco.app/community-guidelines"
    }
  ]
};


export const happinessPageStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "How to Be Happy in Tokyo: Authentic Friendships & Fika",
  "description": "Comprehensive guide to finding happiness through authentic friendships and Swedish Fika tradition in Tokyo",
  "url": "https://connectco.app/happiness",
  "mainEntity": {
    "@type": "HowTo",
    "name": "How to Find Happiness Through Authentic Friendships",
    "description": "Three-step framework for building happiness through meaningful connections and Swedish Fika tradition",
    "image": "https://connectco.app/happiness-how-to.jpg",
    "step": [
      {
        "@type": "HowToStep",
        "name": "Connect",
        "text": "Start with one conversation over coffee through Swedish Fika tradition"
      },
      {
        "@type": "HowToStep", 
        "name": "Share",
        "text": "Be open, listen deeply, and let others in through authentic vulnerability"
      },
      {
        "@type": "HowToStep",
        "name": "Grow",
        "text": "Build friendships that last far beyond the table through consistent connection"
      }
    ]
  }
};

export const happinessFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the connection between friendship and happiness?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Harvard Study of Adult Development, spanning 85+ years, found that good relationships are the strongest predictor of happiness and health. Quality friendships keep us happier, healthier, and more resilient through life's challenges."
      }
    },
    {
      "@type": "Question",
      "name": "How does Swedish Fika create happiness?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Swedish Fika is the practice of slowing down for meaningful conversation over coffee. It creates happiness by fostering authentic connection, reducing social anxiety, and building the foundation for lasting friendships through intentional presence."
      }
    },
    {
      "@type": "Question",
      "name": "How can Connect & Co help me find happiness in Tokyo?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Connect & Co creates opportunities for authentic connection through Swedish Fika traditions, curated small group experiences, and growth circles. Our community focuses on friendship-first approaches that naturally lead to increased happiness and wellbeing."
      }
    }
  ]
};

// Founders 1-on-1 Application Structured Data
export const foundersApplicationStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Founders 1-on-1 Application",
  "alternateName": "Entrepreneur Matchmaking Application",
  "description": "Application form for exclusive 1-on-1 matchmaking between founders, entrepreneurs, and startup operators in Tokyo",
  "url": "https://connectco.app/curated-fika/founders-1on1/apply",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web Browser",
  "browserRequirements": "HTML5, JavaScript enabled",
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png",
      "width": "200",
      "height": "60"
    }
  },
  "datePublished": "2025-11-09",
  "dateModified": "2025-11-09",
  "inLanguage": "en-US",
  "isAccessibleForFree": true,
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "description": "Free application for confidential 1-on-1 founder matching"
  },
  "featureList": [
    "Confidential 1-on-1 matchmaking",
    "AI-assisted compatibility matching",
    "Business challenge-focused pairing",
    "Company stage alignment",
    "Topic and goal matching",
    "Premium Tokyo venue coordination",
    "NDA available for sensitive discussions"
  ],
  "audience": {
    "@type": "Audience",
    "audienceType": "Founders, entrepreneurs, solopreneurs, startup operators",
    "geographicArea": {
      "@type": "Place",
      "name": "Tokyo, Japan"
    }
  },
  "keywords": "founder application, entrepreneur matching, confidential 1-on-1, startup networking, founder support tokyo",
  "about": [
    {
      "@type": "Thing",
      "name": "Founder Networking"
    },
    {
      "@type": "Thing",
      "name": "Entrepreneur Support"
    },
    {
      "@type": "Thing",
      "name": "Confidential Business Discussions"
    },
    {
      "@type": "Thing",
      "name": "Startup Community Building"
    }
  ]
};

// FAQ Schema for Founders Application
export const foundersApplicationFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Founders 1-on-1 Application FAQ",
  "description": "Frequently asked questions about the Founders 1-on-1 application process",
  "url": "https://connectco.app/curated-fika/founders-1on1/apply#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Who is the Founders 1-on-1 program for?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The program is for solo founders, co-founders, solopreneurs, freelancers, early-stage operators, and tech builders in Tokyo who want confidential 1-on-1 conversations with fellow entrepreneurs about real business challenges."
      }
    },
    {
      "@type": "Question",
      "name": "How does the matching process work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We use human curation combined with AI assistance to match founders based on company stage, industry, current challenges, topics of interest, goals, and communication style. Matches are made thoughtfully to ensure compatibility and mutual value."
      }
    },
    {
      "@type": "Question",
      "name": "Is my information kept confidential?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, all applications and conversations are confidential by default. We follow Chatham House Rule and NDAs are available for sensitive discussions about product plans, competitive strategy, or fundraising."
      }
    },
    {
      "@type": "Question",
      "name": "How long does the matching process take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Applications are reviewed within 3-5 business days. Once approved, matching typically takes 1-2 weeks depending on availability and compatibility of other founders in the pool."
      }
    },
    {
      "@type": "Question",
      "name": "What happens during a 1-on-1 session?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Sessions are 60-90 minutes at a premium Tokyo café. No pitches, no small talk—just two builders discussing real challenges, sharing tactical advice, and supporting each other's journey. Sessions are structured around what you're working through right now."
      }
    },
    {
      "@type": "Question",
      "name": "Is there a cost for the program?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The application and matching service are free. Participants cover their own food and beverage at the café. The focus is on creating value through authentic founder connection, not monetization."
      }
    }
  ]
};

// How-To Schema for Application Process
export const foundersApplicationHowToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Apply for Founders 1-on-1 Matching",
  "description": "Step-by-step guide to applying for confidential 1-on-1 matchmaking with fellow entrepreneurs in Tokyo",
  "image": "https://connectco.app/founders-application-how-to.jpg",
  "totalTime": "PT10M",
  "estimatedCost": {
    "@type": "MonetaryAmount",
    "currency": "USD",
    "value": "0"
  },
  "supply": [
    {
      "@type": "HowToSupply",
      "name": "Information about your current role and company stage"
    },
    {
      "@type": "HowToSupply",
      "name": "Description of current business challenges"
    },
    {
      "@type": "HowToSupply",
      "name": "Topics you want to discuss"
    },
    {
      "@type": "HowToSupply",
      "name": "Availability and location preferences"
    }
  ],
  "step": [
    {
      "@type": "HowToStep",
      "name": "Share Personal Information",
      "text": "Provide your name, email, and contact preferences. Optionally share contact info for day-of coordination.",
      "url": "https://connectco.app/curated-fika/founders-1on1/apply#step-1"
    },
    {
      "@type": "HowToStep",
      "name": "Describe What You're Building",
      "text": "Share your role (solo founder, co-founder, etc.), company stage, industry, and what you're currently working through.",
      "url": "https://connectco.app/curated-fika/founders-1on1/apply#step-2"
    },
    {
      "@type": "HowToStep",
      "name": "Select Topics and Goals",
      "text": "Choose business topics you want to discuss (product, growth, fundraising, etc.) and what you're hoping to get from the conversation.",
      "url": "https://connectco.app/curated-fika/founders-1on1/apply#step-3"
    },
    {
      "@type": "HowToStep",
      "name": "Set Preferences and Availability",
      "text": "Indicate your schedule flexibility, location preferences in Tokyo, and date ranges when you're available for a session.",
      "url": "https://connectco.app/curated-fika/founders-1on1/apply#step-4"
    },
    {
      "@type": "HowToStep",
      "name": "Submit and Await Match",
      "text": "Submit your application and receive confirmation. We'll review within 3-5 days and start matching you with compatible founders.",
      "url": "https://connectco.app/curated-fika/founders-1on1/apply#submit"
    }
  ]
};

export const whyConnectCoStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Why Connect & Co: Tokyo's Curated Friendship Community",
  "description": "Competitive positioning page explaining how Connect & Co differs from Meetup, Eventbrite, Bumble BFF, Timeleft, and InterNations through curated small-group fika formats.",
  "url": "https://connectco.app/why-connectco",
  "inLanguage": "en-US",
  "isPartOf": {
    "@type": "WebSite",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "mainEntity": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app",
    "description": "Tokyo's membership-based fika community where locals, expats, and digital nomads build real friendships through intentional, unhurried gatherings inspired by Swedish fika tradition.",
    "foundingDate": "2025",
    "areaServed": {
      "@type": "Place",
      "name": "Tokyo, Japan"
    },
    "knowsAbout": [
      "Swedish Fika Tradition",
      "Curated Social Events",
      "Small Group Connection",
      "Authentic Friendship Building",
      "Expat Community Tokyo"
    ],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Fika Formats",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Monthly Fika",
            "description": "Monthly community gathering for up to 25 people"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Weekly Fika",
            "description": "Weekly intimate gathering for up to 10 people"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Curated Fika",
            "description": "AI-matched intimate gathering for 3-4 people"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Speed Fika",
            "description": "Psychology-based structured conversation event for 30-40 participants across 4 rotating rounds"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Curated Dinners",
            "description": "Small-group shared dining experiences"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Neighborhood Walks",
            "description": "Guided walks through Tokyo's hidden neighborhoods"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Growth Circle Book Club",
            "description": "Monthly book club focused on shared reading and meaningful discussion"
          }
        }
      ]
    }
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://connectco.app"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Why Connect & Co",
        "item": "https://connectco.app/why-connectco"
      }
    ]
  },
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png"
    }
  },
  "about": [
    { "@type": "Thing", "name": "Fika Community Tokyo" },
    { "@type": "Thing", "name": "Meetup Alternative" },
    { "@type": "Thing", "name": "Authentic Friendship Building" },
    { "@type": "Thing", "name": "Expat Social Life Tokyo" },
    { "@type": "Thing", "name": "Swedish Fika Tradition" }
  ],
  "audience": {
    "@type": "Audience",
    "audienceType": "Locals, expats, and digital nomads in Tokyo seeking authentic friendships",
    "geographicArea": {
      "@type": "Place",
      "name": "Tokyo, Japan"
    }
  }
};

export const whyConnectCoFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "Why Connect & Co FAQ",
  "url": "https://connectco.app/why-connectco#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How is Connect & Co different from Meetup?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Meetup optimises for reach and logistics — large open events where you show up and leave without a lasting connection. Connect & Co is a curated membership community with recurring small-group formats (4 to 25 people) designed to turn strangers into friends through repetition and intimacy, not scale."
      }
    },
    {
      "@type": "Question",
      "name": "How is Connect & Co different from Bumble BFF?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Bumble BFF optimises for online matching. An app match is frictionless to start, but it rarely converts to real-life community. Connect & Co is offline-first — recurring in-person rituals where the same faces appear week after week until familiarity becomes friendship."
      }
    },
    {
      "@type": "Question",
      "name": "How is Connect & Co different from InterNations?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "InterNations is networking dressed as socialising — large events with a transactional undertone. Connect & Co is built around the Swedish fika philosophy: no agenda, no business cards, no networking subtext. Friendship, not professional gain, is the explicit goal."
      }
    },
    {
      "@type": "Question",
      "name": "What is the intimacy ladder at Connect & Co?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Connect & Co offers a progression of formats from Monthly Fika (25 people) to Weekly Fika (10 people) to Curated Fika (AI-matched groups of 3-4). Each format is designed for a different depth of connection, so members can move from first hello to close friendship within the same community."
      }
    },
    {
      "@type": "Question",
      "name": "What is Speed Fika?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Speed Fika is Connect & Co's signature large-format event — 30 to 40 participants, 12 psychology-based questions, 4 rotating rounds, 120 minutes total. It's the anti-speed-dating revolution: structured deep conversation that builds authentic connection rather than surface-level small talk."
      }
    },
    {
      "@type": "Question",
      "name": "Who is Connect & Co for?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Connect & Co is for locals, expats, and digital nomads in Tokyo who are done collecting acquaintances and want to build real friendships. No networking. No agenda. Just consistent, honest conversation through the lens of Swedish fika culture."
      }
    }
  ]
};

// Partner Page Structured Data
export const partnerPageStructuredData = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Partner with ConnectCo",
  "description": "Brand partnership and sponsorship opportunities with ConnectCo, Tokyo's membership-based fika community for English-speaking expats and international professionals.",
  "url": "https://connectco.app/partner",
  "inLanguage": "en-US",
  "isPartOf": {
    "@type": "WebSite",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "mainEntity": {
    "@type": "Service",
    "name": "ConnectCo Brand Partnerships",
    "description": "Sponsorship and partnership opportunities reaching English-speaking expats and international professionals in Tokyo through in-person events, newsletter placements, and community endorsements.",
    "provider": {
      "@type": "Organization",
      "name": "Connect & Co",
      "url": "https://connectco.app",
      "logo": {
        "@type": "ImageObject",
        "url": "https://connectco.app/logo.png"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "partnerships",
        "email": "mark@connectco.app",
        "availableLanguage": ["English", "Japanese"]
      },
      "areaServed": {
        "@type": "Place",
        "name": "Tokyo, Japan"
      }
    },
    "serviceType": "Community Sponsorship and Brand Partnership",
    "audience": {
      "@type": "Audience",
      "audienceType": "English-speaking expats, digital nomads, and international professionals aged 25-45 in Tokyo"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Partnership Tiers",
      "itemListElement": [
        {
          "@type": "Offer",
          "name": "Community Partner",
          "description": "Monthly brand presence across ConnectCo communications and website",
          "price": "30000",
          "priceCurrency": "JPY",
          "itemOffered": {
            "@type": "Service",
            "name": "Community Partner Package",
            "description": "Logo on event communications, monthly newsletter mention, listing on ConnectCo Recommended page, monthly post-event report"
          }
        },
        {
          "@type": "Offer",
          "name": "Event Sponsor",
          "description": "Per-event sponsorship with in-room host mention and branded presence",
          "price": "60000",
          "priceCurrency": "JPY",
          "itemOffered": {
            "@type": "Service",
            "name": "Event Sponsor Package",
            "description": "Logo on event page and communications, genuine host mention to 20-40 people, branded element at event, social post, post-event photo report"
          }
        },
        {
          "@type": "Offer",
          "name": "Presenting Sponsor",
          "description": "Monthly presenting partnership with maximum brand ownership",
          "price": "150000",
          "priceCurrency": "JPY",
          "itemOffered": {
            "@type": "Service",
            "name": "Presenting Sponsor Package",
            "description": "Named presenting partnership of recurring format, everything in Event Sponsor tier, featured blog post, member welcome email inclusion, first right of renewal"
          }
        }
      ]
    }
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://connectco.app"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Partner with ConnectCo",
        "item": "https://connectco.app/partner"
      }
    ]
  },
  "author": {
    "@type": "Organization",
    "name": "Connect & Co",
    "url": "https://connectco.app"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Connect & Co",
    "logo": {
      "@type": "ImageObject",
      "url": "https://connectco.app/logo.png"
    }
  }
};

// Partner Page FAQ Schema
export const partnerPageFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "name": "ConnectCo Partnership FAQ",
  "url": "https://connectco.app/partner#faq",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Who is ConnectCo's audience?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "ConnectCo reaches English-speaking expats, digital nomads, and international professionals aged 25-45 living in or regularly visiting Tokyo. The community has 450+ opted-in subscribers and 60+ regular physical event attendees with professional income and a high degree of brand trust in ConnectCo recommendations."
      }
    },
    {
      "@type": "Question",
      "name": "What sponsorship options are available?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Three tiers are available: Community Partner (¥30,000/month) for brand awareness and newsletter placement; Event Sponsor (¥60,000/event) for in-room host mention and branded presence at a single event; and Presenting Sponsor (¥150,000/month) for named presenting partnership of a recurring format including blog coverage and member email inclusion."
      }
    },
    {
      "@type": "Question",
      "name": "Can we sponsor with product instead of cash?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Product sponsorships — samples, free trials, or in-kind goods — are welcome especially for first partnerships. ConnectCo treats product sponsors the same as cash sponsors in terms of the host mention and post-event report, making it a low-risk way to test the audience fit before committing to a paid tier."
      }
    },
    {
      "@type": "Question",
      "name": "What proof do we get that the sponsorship worked?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Every sponsored event includes a post-event report sent within 48 hours: attendance count, event photos, 2-3 genuine member reactions, and social post metrics. Sponsors see exactly what they paid for after every activation."
      }
    },
    {
      "@type": "Question",
      "name": "Do you work with Japanese companies?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. If your product or service is relevant to English-speaking professionals in Tokyo — regardless of whether your company is Japanese or international — there is likely a strong fit with the ConnectCo audience."
      }
    },
    {
      "@type": "Question",
      "name": "How many people attend ConnectCo events?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Events range from 20 to 40 people in-person per event, with 60+ regular repeat attendees across the full event calendar. The community has 450+ opted-in email subscribers. ConnectCo runs a minimum of two monthly events plus weekly gatherings and curated small-group sessions."
      }
    }
  ]
};

// Breadcrumb Schema
export const foundersApplicationBreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://connectco.app"
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "Curated Fika",
      "item": "https://connectco.app/curated-fika"
    },
    {
      "@type": "ListItem",
      "position": 3,
      "name": "Founders & Startups",
      "item": "https://connectco.app/curated-fika/founders"
    },
    {
      "@type": "ListItem",
      "position": 4,
      "name": "Apply",
      "item": "https://connectco.app/curated-fika/founders-1on1/apply"
    }
  ]
};

export default SEO;